import type {
  Assignment,
  AssignmentAllocation,
  AttendanceEvent,
  AvailabilityInterval,
  BookingSegment,
  ChangeProposal,
  CommandEnvelope,
  ImportProposal,
  Person,
  PlanPatch,
  PlanSession,
  VersionFingerprint,
  Property,
  RuleDefinition,
  ServiceDefinition,
  Shift,
  Unit,
  Workspace,
} from "./model";
import {
  assignmentConflicts,
  committedAllocation,
  currentAttendance,
  effectiveAssignmentWindow,
  syncEligibility,
  executionConflicts,
  activeParticipants,
  actualMinutes,
} from "./selectors";
import { applyStayAction, cycleForAssignment, currentFulfillment, deriveStayCycles, recordFulfillment, type StayAction } from './stay-cycles';
import { resolveEstimate } from './estimates';
import { applyRosterSnapshot, markRosterUpdated } from './roster';
import { recordAbsence, endAbsence } from './absence';
import {
  addDays,
  durationMinutes,
  localDate,
  localTime,
  localToInstant,
  overlap,
  operationalNow,
  stableId,
  todayAt,
  validDate,
  validTime,
} from "./time";
import { reconcileRules, validateRule, workspaceWithRule } from "./rules";
import { assertPlanPatchShape, assessPlan, basePlanPatch, normalizePlanPatch, planFingerprint, projectCalendar } from './planning';

const required = <T>(payload: Record<string, unknown>, key: string): T => {
  if (payload[key] === undefined) throw new Error(`Missing ${key}.`);
  return payload[key] as T;
};
const put = <T extends { id: string }>(items: T[], value: T) =>
  items.some((item) => item.id === value.id)
    ? items.map((item) => (item.id === value.id ? value : item))
    : [...items, value];
const instantOK = (value: string) =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const ensureInterval = (start: string, end: string) => {
  if (!instantOK(start) || !instantOK(end) || end <= start)
    throw new Error(
      "The end must be after the start. Use an explicit next-day date for overnight work.",
    );
};
const serviceFor = (state: Workspace, id: string) =>
  state.services.find((item) => item.id === id);
function requireRecordRevision(
  incoming: { revision?: number },
  existing?: { revision?: number },
) {
  if (existing && (incoming.revision ?? 0) !== (existing.revision ?? 0))
    throw new Error(
      "This record changed since it was opened. Refresh the record before saving; your edits are preserved.",
    );
}
function validateAssignment(state: Workspace, assignment: Assignment) {
  if (
    !state.units.some(
      (item) => item.id === assignment.unitId && !item.archived,
    ) ||
    !serviceFor(state, assignment.serviceId)
  )
    throw new Error("Choose an existing unit and service.");
  if (assignment.date !== null && !validDate(assignment.date))
    throw new Error("Choose a valid workday.");
  if (
    !Number.isSafeInteger(assignment.estimateMinutes) ||
    assignment.estimateMinutes <= 0 ||
    !Number.isSafeInteger(assignment.crewSize) ||
    assignment.crewSize < 1
  )
    throw new Error("Duration and crew size must be positive whole numbers.");
  if (
    assignment.earliestStart &&
    assignment.dueAt &&
    assignment.dueAt < assignment.earliestStart
  )
    throw new Error("The deadline must not precede the earliest start.");
  if (assignment.dependsOn?.includes(assignment.id))
    throw new Error("An assignment cannot depend on itself.");
  const visited = new Set<string>();
  const visit = (id: string): void => {
    if (id === assignment.id)
      throw new Error("Assignment dependencies cannot form a cycle.");
    if (visited.has(id)) return;
    visited.add(id);
    const dependency = state.assignments.find((item) => item.id === id);
    if (!dependency)
      throw new Error("Choose existing prerequisite assignments.");
    dependency.dependsOn?.forEach(visit);
  };
  assignment.dependsOn?.forEach(visit);
}
function validateAvailability(
  state: Workspace,
  intervals = state.availability,
) {
  const people = new Set(state.people.map(item => item.id));
  const byPerson = new Map<string, typeof intervals>();
  for (const day of intervals) {
    if (!people.has(day.personId) || !validDate(day.businessDate))
      throw new Error("Choose an existing person and a valid workday.");
    ensureInterval(day.start, day.end);
    const days = byPerson.get(day.personId) ?? [];
    days.push(day); byPerson.set(day.personId, days);
  }
  for (const days of byPerson.values()) {
    days.sort((a, b) => a.start.localeCompare(b.start));
    let latestEnd: string | undefined;
    for (const day of days) {
      if (latestEnd && day.start < latestEnd)
        throw new Error("Workday intervals overlap, including an interval on an adjacent date.");
      if (!latestEnd || day.end > latestEnd) latestEnd = day.end;
    }
  }
}
function validatePlan(state: Workspace, shift: Shift) {
  if (
    !state.properties.some((item) => item.id === shift.propertyId) ||
    !validDate(shift.date)
  )
    throw new Error("Choose a property and valid shift date.");
  ensureInterval(shift.start, shift.end);
  if (new Set(shift.assignmentIds).size !== shift.assignmentIds.length)
    throw new Error("An assignment was selected more than once.");
  for (const id of shift.assignmentIds) {
    const assignment = state.assignments.find((item) => item.id === id);
    if (!assignment || ["declined", "cancelled"].includes(assignment.status))
      throw new Error(
        "A selected assignment is unavailable. Review the current workload.",
      );
    if (
      assignment.status === "completed" &&
      !state.allocations.some(
        (item) =>
          item.assignmentId === id &&
          item.shiftId === shift.id &&
          item.status === "finished",
      )
    )
      throw new Error(
        "A selected assignment is unavailable. Review the current workload.",
      );
    if (
      state.units.find((item) => item.id === assignment.unitId)?.propertyId !==
      shift.propertyId
    )
      throw new Error("A selected unit belongs to another property.");
    const timeZone = state.properties.find(
      (item) => item.id === shift.propertyId,
    )!.timeZone;
    if (
      assignment.date &&
      (assignment.date < localDate(shift.start, timeZone) ||
        assignment.date > localDate(shift.end, timeZone))
    )
      throw new Error(
        "A selected assignment belongs to another workday. Move it explicitly before planning this shift.",
      );
    if (
      state.allocations.some(
        (item) =>
          item.assignmentId === id &&
          item.shiftId !== shift.id &&
          item.status === "committed",
      )
    )
      throw new Error("This assignment is already committed to another shift.");
  }
  for (const position of shift.positions) {
    ensureInterval(position.coverageStart, position.coverageEnd);
    if (
      position.coverageStart < shift.start ||
      position.coverageEnd > shift.end
    )
      throw new Error(
        "Participant coverage must be inside the shift interval.",
      );
    if (!position.personId) continue;
    if (
      !state.people.some(
        (item) => item.id === position.personId && !item.archived,
      )
    )
      throw new Error("A selected person is unavailable or archived.");
    if (
      shift.positions.some(
        (other) =>
          other.id !== position.id &&
          other.personId === position.personId &&
          overlap(
            position.coverageStart,
            position.coverageEnd,
            other.coverageStart,
            other.coverageEnd,
          ),
      )
    )
      throw new Error(
        "One person cannot fill overlapping positions in the same shift.",
      );
    if (
      state.shifts.some(
        (other) =>
          other.id !== shift.id &&
          ["Planned", "Active"].includes(other.status) &&
          other.positions.some(
            (item) =>
              item.personId === position.personId &&
              overlap(
                position.coverageStart,
                position.coverageEnd,
                item.coverageStart,
                item.coverageEnd,
              ),
          ),
      )
    )
      throw new Error(
        "This person is committed to another shift during these hours. Choose different coverage or leave the position unfilled.",
      );
  }
}
function syncAllocations(state: Workspace, shift: Shift): Workspace {
  const allocations = state.allocations.map((item) =>
    item.shiftId === shift.id &&
    item.status === "committed" &&
    !shift.assignmentIds.includes(item.assignmentId) &&
    !state.sessions.some(
      (session) => session.allocationId === item.id && !session.end,
    )
      ? { ...item, status: "released" as const, revision: item.revision + 1 }
      : item,
  );
  for (const assignmentId of shift.assignmentIds) {
    const assignment = state.assignments.find(
      (item) => item.id === assignmentId,
    );
    if (
      assignment &&
      ["completed", "declined", "cancelled"].includes(assignment.status)
    )
      continue;
    if (assignment && assignment.date === null) {
      assignment.date = shift.date;
      assignment.manualDate = true;
      assignment.revision++;
    }
    const existing = allocations.find(
      (item) =>
        item.assignmentId === assignmentId &&
        item.shiftId === shift.id &&
        ["committed", "tentative"].includes(item.status),
    );
    if (existing) existing.status = "committed";
    if (!existing)
      allocations.push({
        id: stableId("allocation", shift.id, assignmentId, shift.revision),
        assignmentId,
        shiftId: shift.id,
        participantIds: [],
        status: "committed",
        revision: 1,
      });
  }
  return { ...state, allocations };
}
function rangeFor(state: Workspace, at = operationalNow(state)) {
  const today = todayAt(state.properties[0]?.timeZone, at);
  const dates = state.shifts
    .filter(
      (item) => item.status !== "Completed" && item.status !== "Cancelled",
    )
    .map((item) => item.date);
  const endDates = [addDays(today, 42), ...dates].sort();
  return {
    from: [today, ...dates].sort()[0],
    to: endDates[endDates.length - 1],
  };
}
function revalidateStays(state: Workspace): Workspace {
  const stays = state.stays.map((stay) => {
    const bookings = state.bookings
      .filter((item) => stay.bookingIds.includes(item.id))
      .sort((a, b) => a.arrival.localeCompare(b.arrival));
    const timeZone = state.properties.find(
      (item) =>
        item.id ===
        state.units.find((unit) => unit.id === bookings[0]?.unitId)?.propertyId,
    )?.timeZone;
    const valid =
      bookings.length === stay.bookingIds.length &&
      bookings.length >= 2 &&
      bookings.every(
        (item) =>
          item.status === "confirmed" && item.unitId === bookings[0].unitId,
      ) &&
      bookings.every(
        (item, index) =>
          index === 0 ||
          localDate(bookings[index - 1].departure, timeZone) ===
            localDate(item.arrival, timeZone),
      );
    return { ...stay, valid };
  });
  return { ...state, stays };
}

function resolveImpact(
  state: Workspace,
  impact: ChangeProposal,
  keep: boolean,
  reason: string,
): void {
  if (impact.status !== "pending")
    throw new Error(
      "This proposal is no longer pending. Refresh the current changes.",
    );
  if (keep && !reason.trim())
    throw new Error(
      "Record why the commitment is kept. Current conflicts remain visible.",
    );
  if (
    !keep &&
    impact.shiftId &&
    state.shifts.some(
      (item) =>
        item.id === impact.shiftId &&
        ["Completed", "Cancelled"].includes(item.status),
    )
  )
    throw new Error(
      "This shift is closed. Review its current follow-up work without rewriting the historical plan.",
    );
  if (
    state.impacts.some(
      (item) =>
        item.id !== impact.id &&
        item.status === "pending" &&
        item.shiftId === impact.shiftId &&
        item.subjectId === impact.subjectId &&
        item.action === impact.action &&
        item.sourceRevision > impact.sourceRevision,
    )
  )
    throw new Error(
      "A newer proposal supersedes this change. Refresh the current impact review.",
    );
  if (!keep && impact.assignmentId) {
    const assignment = state.assignments.find(
      (item) => item.id === impact.assignmentId,
    );
    if (
      assignment &&
      !["in-progress", "completed"].includes(assignment.status) &&
      !state.sessions.some(
        (item) => item.assignmentId === assignment.id && !item.end,
      )
    ) {
      if (impact.action === "release-work") {
        state.allocations = state.allocations.map((item) =>
          item.assignmentId === assignment.id &&
          item.shiftId === impact.shiftId &&
          item.status === "committed"
            ? { ...item, status: "released" }
            : item,
        );
        state.shifts = state.shifts.map((item) =>
          item.id === impact.shiftId
            ? {
                ...item,
                assignmentIds: item.assignmentIds.filter(
                  (aid) => aid !== assignment.id,
                ),
                revision: item.revision + 1,
              }
            : item,
        );
        if (
          assignment.origin !== "manual" &&
          !assignment.manualDate &&
          assignment.occurrenceIds.length &&
          assignment.occurrenceIds.every(
            (id) =>
              state.occurrences.find((item) => item.id === id)?.status ===
              "superseded",
          ) &&
          !state.sessions.some((item) => item.assignmentId === assignment.id)
        )
          assignment.status = "cancelled";
      } else if (impact.proposedAssignment) {
        const proposed = impact.proposedAssignment;
        // Live deadlines remain current; acceptance changes the scheduling commitment only.
        assignment.date = proposed.date;
        if(assignment.estimateProvenance?.confidence!=='unrecorded')assignment.estimateMinutes = proposed.estimateMinutes;
        assignment.requiredSkills = proposed.requiredSkills;
        assignment.revision++;
        const shift = state.shifts.find((item) => item.id === impact.shiftId);
        if (shift && assignment.date !== shift.date) {
          state.allocations = state.allocations.map((item) =>
            item.assignmentId === assignment.id && item.status === "committed"
              ? { ...item, status: "released" }
              : item,
          );
          shift.assignmentIds = shift.assignmentIds.filter(
            (aid) => aid !== assignment.id,
          );
        }
      }
    }
  }
  impact.status = keep ? "kept" : "applied";
  impact.reason = reason;
}

function addFactImpacts(
  previous: Workspace,
  next: Workspace,
  envelope: CommandEnvelope,
): Workspace {
  if (
    envelope.type.startsWith("impact.") ||
    envelope.type.startsWith("shift.") ||
    envelope.type.startsWith("allocation.") ||
    envelope.type.startsWith("work.") ||
    envelope.type.startsWith("plan.session.") ||
    // A placement-only commit changes commitments, not unit/workforce facts or demand.
    (envelope.type === "plan.apply" && !(envelope.payload.patch as PlanPatch).calendarChanges.length) ||
    envelope.type === "linen.record"
  )
    return next;
  const impacts = [...next.impacts];
  const previousUnits = new Map(previous.units.map(item => [item.id, item]));
  const previousPeople = new Map(previous.people.map(item => [item.id, item]));
  const previousAvailability = new Map(previous.availability.map(item => [item.id, item]));
  const nextAvailability = new Map(next.availability.map(item => [item.id, item]));
  const previousAssignments = new Map(previous.assignments.map(item => [item.id, item]));
  const nextAssignments = new Map(next.assignments.map(item => [item.id, item]));
  const previousAttendanceIds = new Set(previous.attendance.map(item => item.id));
  const changedUnits = next.units.filter((item) => {
    const old = previousUnits.get(item.id);
    return (
      old &&
      JSON.stringify([
        old.access,
        old.cleanliness,
        old.tags,
        old.standardMinutes,
        old.archived,
        old.permissionGranted,
      ]) !==
        JSON.stringify([
          item.access,
          item.cleanliness,
          item.tags,
          item.standardMinutes,
          item.archived,
          item.permissionGranted,
        ])
    );
  });
  const changedPeople = next.people.filter((item) => {
    const old = previousPeople.get(item.id);
    return (
      old &&
      JSON.stringify([old.skills, old.duties, old.archived,old.requiresBuddy,old.buddyRestriction]) !==
        JSON.stringify([item.skills, item.duties, item.archived,item.requiresBuddy,item.buddyRestriction])
    );
  });
  const changedAvailability = [
    ...previous.availability,
    ...next.availability,
  ].filter(
    (item) =>
      JSON.stringify(
        previousAvailability.get(item.id),
      ) !== JSON.stringify(nextAvailability.get(item.id)),
  );
  const changedAttendance = next.attendance.filter(
    (item) => !previousAttendanceIds.has(item.id),
  );
  for (const shift of next.shifts.filter((item) =>
    ["Planned", "Active"].includes(item.status),
  )) {
    const assignments = next.assignments.filter((item) =>
      shift.assignmentIds.includes(item.id),
    );
    const units = changedUnits.filter((item) =>
      assignments.some((assignment) => assignment.unitId === item.id),
    );
    const timeZone = next.properties.find(
      (item) => item.id === shift.propertyId,
    )?.timeZone;
    const people = next.people.filter(
      (person) =>
        shift.positions.some((position) => position.personId === person.id) &&
        (changedPeople.some((item) => item.id === person.id) ||
          changedAvailability.some(
            (interval) =>
              interval.personId === person.id &&
              (interval.businessDate === shift.date ||
                overlap(interval.start, interval.end, shift.start, shift.end)),
          ) ||
          changedAttendance.some(
            (item) =>
              item.personId === person.id &&
              (item.shiftId
                ? item.shiftId === shift.id
                : localDate(item.at, timeZone) === shift.date),
          )),
    );
    for (const subject of [
      ...units.map((unit) => ({
        id: unit.id,
        type: "unit",
        name: unit.name,
        before: JSON.stringify(
          previousUnits.get(unit.id),
        ),
        after: JSON.stringify(unit),
      })),
      ...people.map((person) => ({
        id: person.id,
        type: "person",
        name: person.name,
        before: JSON.stringify({
          person: previousPeople.get(person.id),
          availability: previous.availability.filter(
            (item) => item.personId === person.id,
          ),
          attendance: previous.attendance
            .filter((item) => item.personId === person.id)
            .slice(-1),
        }),
        after: JSON.stringify({
          person,
          availability: next.availability.filter(
            (item) => item.personId === person.id,
          ),
          attendance: next.attendance
            .filter((item) => item.personId === person.id)
            .slice(-1),
        }),
      })),
    ]) {
      const id = stableId(
        "impact",
        shift.id,
        subject.type,
        subject.id,
        subject.after,
      );
      for (let i = 0; i < impacts.length; i++)
        if (
          impacts[i].status === "pending" &&
          impacts[i].shiftId === shift.id &&
          impacts[i].subjectType === subject.type &&
          impacts[i].subjectId === subject.id
        )
          impacts[i] = { ...impacts[i], status: "superseded" };
      impacts.push({
        id,
        shiftId: shift.id,
        propertyId: shift.propertyId,
        date: shift.date,
        subjectType: subject.type,
        subjectId: subject.id,
        title: `${subject.name}: current facts changed`,
        before: subject.before,
        after: subject.after,
        action: subject.type === "person" ? "reassign" : "fact-change",
        status: "pending",
        severity: "warning",
        createdAt: envelope.at!,
        sourceRevision: next.revision,
      });
    }
  }
  for (const oldAllocation of previous.allocations.filter(
    (item) => item.status === "committed",
  )) {
    const before = previousAssignments.get(oldAllocation.assignmentId);
    const after = nextAssignments.get(oldAllocation.assignmentId);
    if (
      !before ||
      !after ||
      (before.date === after.date && before.status === after.status)
    )
      continue;
    if (
      !["assignment.move", "assignment.decline", "assignment.bulk"].includes(
        envelope.type,
      )
    )
      continue;
    const shift = next.shifts.find((item) => item.id === oldAllocation.shiftId);
    if (!shift) continue;
    impacts.push({
      id: stableId("assignment-change", envelope.id, before.id, shift.id),
      shiftId: shift.id,
      propertyId: shift.propertyId,
      date: shift.date,
      subjectType: "assignment",
      subjectId: before.id,
      assignmentId: before.id,
      title: "Assignment commitment changed",
      before: `${before.date ?? "Backlog"} · ${before.status}`,
      after: `${after.date ?? "Backlog"} · ${after.status}`,
      action: "fact-change",
      status: "pending",
      severity: "info",
      createdAt: envelope.at!,
      sourceRevision: next.revision,
    });
  }
  const newWork = next.assignments.filter(
    (item) =>
      !previousAssignments.has(item.id) &&
      item.status === "open",
  );
  for (const assignment of newWork) {
    const propertyId = next.units.find(
      (item) => item.id === assignment.unitId,
    )?.propertyId;
    if (!propertyId) continue;
    const id = stableId("new-work", assignment.id);
    if (!impacts.some((item) => item.id === id))
      impacts.push({
        id,
        propertyId,
        date: assignment.date ?? todayAt(next.properties.find(item => item.id === propertyId)?.timeZone, envelope.at),
        subjectType: "assignment",
        subjectId: assignment.id,
        assignmentId: assignment.id,
        title: "New required work is available",
        before: "Not in the workload",
        after:
          next.services.find((item) => item.id === assignment.serviceId)
            ?.name ?? "New service",
        action: "new-work",
        status: "pending",
        severity: "info",
        createdAt: envelope.at!,
        sourceRevision: next.revision,
      });
  }
  return { ...next, impacts };
}

export function validateWorkspace(state: Workspace): void {
  if (!state || typeof state !== "object")
    throw new Error("The saved workspace is not readable.");
  if (state.schemaVersion !== 2 || !Number.isSafeInteger(state.revision))
    throw new Error("Unsupported workspace version.");
  for (const name of [
    "properties",
    "units",
    "people",
    "services",
    "bookings",
    "stays",
    "rules",
    "ruleDrafts",
    "occurrences",
    "assignments",
    "availability",
    "attendance",
    "shifts",
    "allocations",
    "sessions",
    "linen",
    "impacts",
    "syncIntents",
    "importRuns",
    "audit",
    "facts",
  ] as const) {
    if (
      !Array.isArray(state[name]) ||
      state[name].some(
        (item) =>
          !item || typeof item !== "object" || typeof item.id !== "string",
      ) ||
      new Set(state[name].map((item) => item.id)).size !== state[name].length
    )
      throw new Error(`Invalid or duplicate ${name} identities.`);
  }
  const propertyIds = new Set(state.properties.map(item => item.id));
  const unitIds = new Set(state.units.map(item => item.id));
  const personIds = new Set(state.people.map(item => item.id));
  const serviceIds = new Set(state.services.map(item => item.id));
  const bookingIds = new Set(state.bookings.map(item => item.id));
  for(const name of ['stayCycles','fulfillments','rosterSnapshots','rosterActions','absences'] as const){const records=state[name];if(records!==undefined&&(!Array.isArray(records)||records.some(item=>!item||typeof item.id!=='string')||new Set(records.map(item=>item.id)).size!==records.length))throw new Error(`Invalid or duplicate ${name} identities.`);}
  for(const cycle of state.stayCycles??[])if(!unitIds.has(cycle.unitId)||!bookingIds.has(cycle.anchorBookingId)||!Array.isArray(cycle.bookingIds)||cycle.bookingIds.some(id=>!bookingIds.has(id))||!Number.isSafeInteger(cycle.serviceRevision)||cycle.serviceRevision<1||typeof cycle.sourceFingerprint!=='string'||cycle.earlyAuthorization&&(!instantOK(cycle.earlyAuthorization.from)||cycle.earlyAuthorization.to!==undefined&&!instantOK(cycle.earlyAuthorization.to))||cycle.checkout&&!instantOK(cycle.checkout.at))throw new Error('Invalid reservation cycle.');
  for(const fulfillment of state.fulfillments??[])if(!(state.stayCycles??[]).some(item=>item.id===fulfillment.stayCycleId)||!state.assignments.some(item=>item.id===fulfillment.assignmentId)||!instantOK(fulfillment.completedAt)||!Array.isArray(fulfillment.occurrenceIds))throw new Error('Invalid service fulfillment.');
  for(const snapshot of state.rosterSnapshots??[])if(!Array.isArray(snapshot.rows)||snapshot.rows.some(row=>!personIds.has(row.personId)||!validDate(row.businessDate)||!instantOK(row.start)||!instantOK(row.end)||row.end<=row.start)||!validDate(snapshot.from)||!validDate(snapshot.to))throw new Error('Invalid source roster snapshot.');
  for(const action of state.rosterActions??[])if(!personIds.has(action.personId)||typeof action.fingerprint!=='string'||!validDate(action.businessDate)||!instantOK(action.recordedAt))throw new Error('Invalid external roster action.');
  for(const absence of state.absences??[])if(!personIds.has(absence.personId)||!instantOK(absence.start)||!instantOK(absence.end)||absence.end<=absence.start||!instantOK(absence.reportedAt)||!['sickness','unpaid-leave','no-show','other','unspecified'].includes(absence.reason))throw new Error('Invalid operational absence.');
  const assignmentIds = new Set(state.assignments.map(item => item.id));
  const shiftIds = new Set(state.shifts.map(item => item.id));
  const stringArray = (value: unknown): value is string[] =>
    Array.isArray(value) && value.every((item) => typeof item === "string");
  if (
    !stringArray(state.idempotencyKeys) ||
    !Array.isArray(state.legacyArchive)
  )
    throw new Error("Invalid workspace recovery or transaction records.");
  if (
    !state.integrationPolicies?.hotellinx ||
    !["always", "never", "checked-out"].includes(
      state.integrationPolicies.hotellinx.cleaningSync,
    )
  )
    throw new Error("Invalid integration policy.");
  for (const property of state.properties) {
    if (
      typeof property.name !== "string" ||
      !property.name.trim() ||
      typeof property.timeZone !== "string"
    )
      throw new Error("A property needs a name and timezone.");
    new Intl.DateTimeFormat("en", { timeZone: property.timeZone }).format(
      new Date(0),
    );
  }
  for (const unit of state.units)
    if (
      !propertyIds.has(unit.propertyId) ||
      typeof unit.name !== "string" ||
      !unit.name.trim() ||
      !stringArray(unit.tags) ||
      !stringArray(unit.disabledInheritedRuleIds) ||
      !["vacant", "occupied", "unknown"].includes(unit.access) ||
      !["dirty", "cleaned", "inspection-needed", "ready", "unknown"].includes(
        unit.cleanliness,
      )
    )
      throw new Error(
        "A unit needs an existing property, a name and valid operational facts.",
      );
  for (const person of state.people)
    if (
      typeof person.name !== "string" ||
      !person.name.trim() ||
      !stringArray(person.skills) ||
      !stringArray(person.duties) ||
      !person.externalIds ||
      typeof person.externalIds !== "object" ||
      person.requiresBuddy!==undefined&&typeof person.requiresBuddy!=='boolean' ||
      person.buddyRestriction&&(!instantOK(person.buddyRestriction.from)||person.buddyRestriction.until!==undefined&&(!instantOK(person.buddyRestriction.until)||person.buddyRestriction.until<=person.buddyRestriction.from))
    )
      throw new Error("Invalid person identity or capabilities.");
  for (const service of state.services)
    if (
      typeof service.name !== "string" ||
      !stringArray(service.requiredSkills) ||
      !Number.isSafeInteger(service.estimateMinutes) ||
      service.estimateMinutes < 1 ||
      !Number.isSafeInteger(service.crewSize) ||
      service.crewSize < 1 ||
      !["vacant", "permission", "none"].includes(service.access)
    )
      throw new Error("Invalid service definition.");
  for (const booking of state.bookings) {
    if (
      !unitIds.has(booking.unitId) ||
      typeof booking.guest !== "string" ||
      !["confirmed", "cancelled", "no-show"].includes(booking.status)
    )
      throw new Error("Invalid booking identity or status.");
    ensureInterval(booking.arrival, booking.departure);
  }
  for (const stay of state.stays)
    if (
      !stringArray(stay.bookingIds) ||
      stay.bookingIds.some(
        (id) => !bookingIds.has(id),
      )
    )
      throw new Error("Invalid continuous-stay references.");
  for (const rule of [
    ...state.rules,
    ...state.ruleDrafts,
    ...(state.ruleHistory ?? []),
  ]) {
    if (
      !rule.trigger ||
      !rule.conditions ||
      !rule.window ||
      !Array.isArray(rule.conditions.weekdays) ||
      !Array.isArray(rule.conditions.months) ||
      !stringArray(rule.conditions.tags) ||
      !stringArray(rule.conditions.unitTypes) ||
      !Array.isArray(rule.window.allowedWeekdays) ||
      !validTime(rule.window.startTime) ||
      !validTime(rule.window.endTime) ||
      !validDate(rule.effectiveFrom) ||
      !["draft", "active", "retired"].includes(rule.status)
    )
      throw new Error("Invalid saved rule definition.");
  }
  for (const assignment of state.assignments) {
    if (
      !unitIds.has(assignment.unitId) ||
      !serviceIds.has(assignment.serviceId)
    )
      throw new Error(
        "An assignment has an invalid unit or service reference.",
      );
    if (
      !stringArray(assignment.requiredSkills) ||
      !stringArray(assignment.preferredSkills) ||
      !stringArray(assignment.occurrenceIds) ||
      ![
        "open",
        "in-progress",
        "paused",
        "completed",
        "declined",
        "cancelled",
      ].includes(assignment.status) ||
      (assignment.date !== null && !validDate(assignment.date)) ||
      !Number.isSafeInteger(assignment.estimateMinutes) ||
      assignment.estimateMinutes < 1 ||
      (assignment.estimateSource!==undefined&&!['unit','service','fixed'].includes(assignment.estimateSource)) ||
      (assignment.estimateProvenance!==undefined&&(!assignment.estimateProvenance||!['unit','service','fixed'].includes(assignment.estimateProvenance.source)||!Number.isSafeInteger(assignment.estimateProvenance.minutes)||assignment.estimateProvenance.minutes<1||typeof assignment.estimateProvenance.label!=='string'||(assignment.estimateProvenance.confidence!==undefined&&!['recorded','unrecorded'].includes(assignment.estimateProvenance.confidence))))
    )
      throw new Error("Invalid assignment fields or lifecycle.");
  }
  for (const occurrence of state.occurrences)
    if (
      !unitIds.has(occurrence.unitId) ||
      !serviceIds.has(occurrence.serviceId) ||
      !validDate(occurrence.nominalDate) ||
      !stringArray(occurrence.reasons) ||
      ![
        "pending",
        "satisfied",
        "suppressed",
        "superseded",
        "needs-information",
        "needs-scheduling",
      ].includes(occurrence.status)
    )
      throw new Error("Invalid generated work requirement.");
  for (const interval of state.availability) {
    if (
      !personIds.has(interval.personId) ||
      !validDate(interval.businessDate)
    )
      throw new Error("Invalid workforce interval reference.");
    ensureInterval(interval.start, interval.end);
  }
  for (const event of state.attendance)
    if (
      !personIds.has(event.personId) ||
      !["expected", "confirmed", "present", "absent"].includes(event.status) ||
      !instantOK(event.at)
    )
      throw new Error("Invalid attendance event.");
  for (const shift of state.shifts) {
    if (
      !propertyIds.has(shift.propertyId) ||
      !Array.isArray(shift.positions) ||
      !stringArray(shift.assignmentIds) ||
      !["Draft", "Planned", "Active", "Completed", "Cancelled"].includes(
        shift.status,
      ) ||
      !validDate(shift.date)
    )
      throw new Error("Invalid shift structure.");
    ensureInterval(shift.start, shift.end);
    for (const position of shift.positions) {
      if (
        !stringArray(position.requiredSkills) ||
        (position.personId &&
          !personIds.has(position.personId))
      )
        throw new Error("Invalid staffing position.");
      ensureInterval(position.coverageStart, position.coverageEnd);
    }
  }
  const active = state.allocations.filter(
    (item) => item.status === "committed",
  );
  if (new Set(active.map((item) => item.assignmentId)).size !== active.length)
    throw new Error("An assignment cannot be committed to two shifts.");
  for (const allocation of state.allocations) {
    if (
      !assignmentIds.has(allocation.assignmentId) ||
      !shiftIds.has(allocation.shiftId) ||
      !stringArray(allocation.participantIds) ||
      !["tentative", "committed", "released", "finished"].includes(
        allocation.status,
      )
    )
      throw new Error(
        "An allocation has an invalid assignment or shift reference.",
      );
    if (allocation.start || allocation.end)
      ensureInterval(allocation.start!, allocation.end!);
  }
  for (const session of state.sessions)
    if (
      !assignmentIds.has(session.assignmentId) ||
      !stringArray(session.personIds) ||
      !instantOK(session.start) ||
      (session.end && (!instantOK(session.end) || session.end < session.start))
    )
      throw new Error("Invalid actual work session.");
  for (const event of state.linen)
    if (
      !unitIds.has(event.unitId) ||
      !instantOK(event.at) ||
      !["deliver", "collect", "return-check", "return"].includes(
        event.operation,
      )
    )
      throw new Error("Invalid linen operation history.");
  for (const impact of state.impacts)
    if (
      typeof impact.title !== "string" ||
      typeof impact.before !== "string" ||
      typeof impact.after !== "string" ||
      !["pending", "applied", "kept", "superseded"].includes(impact.status) ||
      !["info", "warning", "blocker"].includes(impact.severity)
    )
      throw new Error("Invalid change proposal.");
  for (const intent of state.syncIntents)
    if (
      !["eligible", "held", "failed", "stale", "simulated"].includes(
        intent.status,
      ) ||
      typeof intent.reason !== "string"
    )
      throw new Error("Invalid synchronization intent.");
  for (const run of state.importRuns)
    if (
      typeof run.name !== "string" ||
      typeof run.hash !== "string" ||
      !validDate(run.from) ||
      !validDate(run.to) ||
      !stringArray(run.notes)
    )
      throw new Error("Invalid import run history.");
  for (const fact of state.facts)
    if (
      typeof fact.entityId !== "string" ||
      typeof fact.key !== "string" ||
      !instantOK(fact.observedAt) ||
      !instantOK(fact.recordedAt)
    )
      throw new Error("Invalid operational fact provenance.");
  if (
    state.handovers !== undefined &&
    (!Array.isArray(state.handovers) ||
      state.handovers.some(
        (item) =>
          !item ||
          !assignmentIds.has(item.assignmentId) ||
          !["open", "resolved"].includes(item.status),
      ))
  )
    throw new Error("Invalid handover records.");
  if (state.id !== undefined && (typeof state.id !== 'string' || !state.id) || state.demoClockAt !== undefined && !instantOK(state.demoClockAt)) throw new Error('Invalid workspace identity or operational clock.');
  if (state.planSessions !== undefined) {
    if (!Array.isArray(state.planSessions) || new Set(state.planSessions.map(item => item.id)).size !== state.planSessions.length) throw new Error('Invalid planning session identities.');
    for (const session of state.planSessions) {
      if (!session || typeof session.id !== 'string' || typeof session.actorId !== 'string' || typeof session.workspaceId !== 'string' || state.id && session.workspaceId !== state.id || !['editing', 'applied', 'discarded'].includes(session.state) || !Number.isSafeInteger(session.revision) || session.revision < 1 || typeof session.fingerprint?.value !== 'string' || !instantOK(session.updatedAt) || session.shiftId !== session.patch?.shift.id) throw new Error('Invalid saved planning session.');
      assertPlanPatchShape(session.patch);
    }
  }
}
export function isWorkspace(value: unknown): value is Workspace {
  try {
    validateWorkspace(value as Workspace);
    return true;
  } catch {
    return false;
  }
}

/** Every state transition is idempotent, revision checked, auditable and ready for one repository transaction. */
export function applyCommand(
  input: Workspace,
  original: CommandEnvelope,
): Workspace {
  if (original.workspaceId !== undefined && original.workspaceId !== input.id) throw new Error('This command belongs to another workspace. Reopen the intended workspace before applying it.');
  if (
    input.idempotencyKeys.includes(original.id) ||
    input.audit.some((item) => item.id === original.id)
  )
    return input;
  if (
    !original.id ||
    typeof original.id !== "string" ||
    (original.at !== undefined && !instantOK(original.at))
  )
    throw new Error(
      "A command needs a stable identity and valid effective time.",
    );
  if (
    original.expectedRevision !== undefined &&
    input.revision !== original.expectedRevision
  )
    throw new Error(
      "The workspace changed since this preview. Refresh it before applying; your edits are preserved.",
    );
  const envelope = {
    ...original,
    actor: original.actor ?? "Dispatcher",
    at: new Date(operationalNow(input, original.at)).toISOString(),
  };
  let state = structuredClone(input);
  const payload = envelope.payload;
  const id = (key: string) => required<string>(payload, key);
  const assignmentById = () => {
    const item = state.assignments.find(
      (item) => item.id === id("assignmentId"),
    );
    if (!item) throw new Error("Assignment not found.");
    return item;
  };
  const shiftById = () => {
    const item = state.shifts.find((item) => item.id === id("shiftId"));
    if (!item) throw new Error("Shift not found.");
    return item;
  };
  let regenerate = false;
  let regenerationRange: { from: string; to: string } | undefined;
  switch (envelope.type) {
    case 'practice.clock': {
      if(state.kind!=='practice')throw new Error('Practice time can only advance inside a practice workspace.');
      const at=id('at');
      if(!instantOK(at)||at<operationalNow(state))throw new Error('Choose a valid future practice time.');
      state.demoClockAt=at;
      break;
    }
    case 'cycle.authorize-early':
    case 'cycle.confirm-checkout':
    case 'cycle.link-completion':
    case 'cycle.add-return-cleaning': {
      const type=({'cycle.authorize-early':'early','cycle.confirm-checkout':'checkout','cycle.link-completion':'link','cycle.add-return-cleaning':'return'} as const)[envelope.type];
      state=applyStayAction(state,{...payload,type,cycleId:id('cycleId')} as StayAction & {fingerprint?:string},{id:envelope.id,at:envelope.at!,actor:envelope.actor!});
      regenerate=type==='early'||type==='link';
      break;
    }
    case 'roster.mark-external':
      state=markRosterUpdated(state,payload as unknown as Parameters<typeof markRosterUpdated>[1],envelope.at!,envelope.actor!);break;
    case 'absence.record':
      state=recordAbsence(state,payload as unknown as Parameters<typeof recordAbsence>[1],envelope.at!,envelope.actor!);validateAvailability(state);break;
    case 'absence.end':
      state=endAbsence(state,payload as unknown as Parameters<typeof endAbsence>[1],envelope.at!,envelope.actor!);validateAvailability(state);break;
    case 'handover.assign': {
      const handover=state.handovers?.find(item=>item.id===id('handoverId'));
      if(!handover||handover.status!=='open')throw new Error('Choose an unresolved handover.');
      const ownerId=typeof payload.ownerId==='string'&&payload.ownerId?payload.ownerId:undefined;
      const owner=state.people.find(item=>item.id===ownerId&&!item.archived);
      if(ownerId&&!owner)throw new Error('Choose an available person to own the handover.');
      handover.ownerId=ownerId;handover.ownerLabel=owner?.name??envelope.actor!;
      break;
    }
    case 'plan.session.save': {
      const incoming = required<PlanSession>(payload, 'session');
      if (!incoming.id || !incoming.actorId || typeof incoming.fingerprint?.value !== 'string' || incoming.workspaceId !== (state.id ?? 'legacy') || incoming.shiftId !== incoming.patch.shift.id || incoming.state !== 'editing' || !Array.isArray(incoming.patch.allocations) || !Array.isArray(incoming.patch.shift.assignmentIds) || !Array.isArray(incoming.patch.shift.positions) || !Array.isArray(incoming.patch.calendarChanges) || !Array.isArray(incoming.patch.releases)) throw new Error('Choose an editable planning session in this workspace.');
      const old = state.planSessions?.find(item => item.id === incoming.id);
      if (old && (incoming.revision !== old.revision || old.state !== 'editing' || old.actorId !== incoming.actorId || old.shiftId !== incoming.shiftId)) throw new Error('This planning session changed. Reload its current proposal; your edits are preserved.');
      if (!old && incoming.revision !== 0) throw new Error('A new planning session must start at revision zero.');
      const shift = state.shifts.find(item => item.id === incoming.shiftId);
      const stale = old ? old.needsRebase || planFingerprint(state, old.patch).value !== old.fingerprint.value : incoming.fingerprint.value !== planFingerprint(state, incoming.patch).value || incoming.baseShiftRevision !== undefined && incoming.baseShiftRevision !== shift?.revision;
      state.planSessions = put(state.planSessions ?? [], structuredClone({ ...incoming, revision: (old?.revision ?? 0) + 1, updatedAt: envelope.at!, fingerprint: planFingerprint(state, incoming.patch), baseShiftRevision: payload.rebase === true ? shift?.revision : old ? old.baseShiftRevision : incoming.baseShiftRevision ?? shift?.revision, needsRebase: payload.rebase === true ? false : !!stale }));
      break;
    }
    case 'plan.session.discard': {
      const session = state.planSessions?.find(item => item.id === id('sessionId'));
      if (!session || session.state !== 'editing' || payload.revision !== undefined && payload.revision !== session.revision) throw new Error('Choose the current editable planning session.');
      session.state = 'discarded'; session.revision++; session.updatedAt = envelope.at!;
      break;
    }
    case 'plan.apply': {
      const incomingPatch = required<PlanPatch>(payload, 'patch'); const fingerprint = required<VersionFingerprint>(payload, 'fingerprint');
      const patch = normalizePlanPatch(state, incomingPatch);
      const session = typeof payload.sessionId === 'string' ? state.planSessions?.find(item => item.id === payload.sessionId) : undefined;
      if (payload.sessionId && (!session || session.state !== 'editing' || session.revision !== payload.sessionRevision || session.shiftId !== patch.shift.id)) throw new Error('This planning session changed. Reload its current proposal; your edits are preserved.');
      if (session?.needsRebase || session && planFingerprint(state, session.patch).value !== session.fingerprint.value || !fingerprint || fingerprint.value !== planFingerprint(state, incomingPatch).value) throw new Error('The plan preview is stale. Review current facts and refresh the proposal before applying.');
      const oldShift = state.shifts.find(item => item.id === patch.shift.id);
      if (payload.baseShiftRevision !== undefined && payload.baseShiftRevision !== oldShift?.revision || session?.baseShiftRevision !== undefined && session.baseShiftRevision !== oldShift?.revision) throw new Error('The committed shift changed after this proposal. Rebase before applying.');
      const assessment = assessPlan(state, patch, { at: envelope.at!, actorId: envelope.actor });
      if (!assessment.canApply) throw new Error(assessment.structuralBlockers[0].message);
      if (payload.evaluatedAt && patch.allocations.some(item => { const old = state.allocations.find(other => other.id === item.id); return item.start && item.start < envelope.at! && (!old || old.start !== item.start || old.end !== item.end) && !state.sessions.some(session => session.assignmentId === item.assignmentId && !session.end); })) throw new Error('The repair preview expired as operational time advanced. Refresh it before applying.');
      const reviewedImpactIds = (payload.reviewedImpactIds ?? []) as string[];
      const reviewed = reviewedImpactIds.map(impactId => state.impacts.find(item => item.id === impactId));
      if (reviewed.some(item => !item || item.status !== 'pending' || item.shiftId && item.shiftId !== patch.shift.id)) throw new Error('A reviewed change is no longer current. Refresh the selected proposals.');
      for (const impact of reviewed as ChangeProposal[]) if (state.impacts.some(item => item.id !== impact.id && item.status === 'pending' && item.subjectId === impact.subjectId && item.action === impact.action && item.sourceRevision > impact.sourceRevision)) throw new Error('A newer proposal supersedes this change. Refresh the current impact review.');
      if (patch.calendarChanges.length) state.availability = projectCalendar(state.availability, patch.calendarChanges);
      validateAvailability(state);
      const shift: Shift = { ...(oldShift ?? {}), ...structuredClone(patch.shift), status: oldShift?.status === 'Active' ? 'Active' : 'Planned', revision: (oldShift?.revision ?? 0) + 1 };
      state.shifts = put(state.shifts, shift);
      for (const move of patch.workDates ?? []) { const assignment = state.assignments.find(item => item.id === move.assignmentId)!; assignment.date = move.date; assignment.manualDate = true; assignment.revision++; if (move.reason) assignment.notes = [assignment.notes, move.reason].filter(Boolean).join('\n'); }
      const oldAllocations = state.allocations.filter(item => item.shiftId === shift.id && ['committed', 'tentative'].includes(item.status));
      for (const old of oldAllocations) {
        const proposed = patch.allocations.find(item => item.assignmentId === old.assignmentId && !patch.releases.includes(item.id));
        if (!proposed || proposed.id !== old.id || !shift.assignmentIds.includes(old.assignmentId) || state.sessions.some(item => item.allocationId === old.id) && JSON.stringify([old.start, old.end, old.participantIds]) !== JSON.stringify([proposed.start, proposed.end, proposed.participantIds])) { old.status = 'released'; old.revision++; }
      }
      for (const proposed of patch.allocations) {
        if (patch.releases.includes(proposed.id)) continue;
        const assignment = state.assignments.find(item => item.id === proposed.assignmentId)!;
        const originalAllocation = state.allocations.find(item => item.id === proposed.id);
        const historical = originalAllocation && state.sessions.some(item => item.allocationId === originalAllocation.id) && originalAllocation.status === 'released';
        const allocationId = historical ? stableId('continuation', envelope.id, proposed.id) : proposed.id;
        const unchanged = originalAllocation?.status === 'committed' && !historical && JSON.stringify([originalAllocation.start, originalAllocation.end, originalAllocation.participantIds]) === JSON.stringify([proposed.start, proposed.end, proposed.participantIds]);
        if (!unchanged) state.allocations = put(state.allocations, { ...structuredClone(proposed), id: allocationId, status: 'committed', revision: (historical ? 0 : originalAllocation?.revision ?? 0) + 1 });
        if (assignment.date === null) { assignment.date = shift.date; assignment.manualDate = true; assignment.revision++; }
      }
      validatePlan(state, shift);
      for (const impact of reviewed as ChangeProposal[]) { impact.status = 'applied'; impact.reason = String(payload.reviewReason ?? 'Reviewed with the applied plan. Current conflicts remain visible.'); }
      if (session) {
        const appliedWholeSession = payload.keepSession !== true;
        session.state = appliedWholeSession ? 'applied' : 'editing'; session.revision++; session.updatedAt = envelope.at!; session.baseShiftRevision = shift.revision; session.needsRebase = false;
        if (appliedWholeSession) session.patch = basePlanPatch(state, shift);
        else {
          // Rebase the remaining proposal while retaining all unapplied user choices.
          const appliedAssignments = new Set(patch.allocations.map(item => item.assignmentId));
          const committed = basePlanPatch(state, shift);
          session.patch.allocations = session.patch.allocations.map(item => appliedAssignments.has(item.assignmentId) ? committed.allocations.find(other => other.assignmentId === item.assignmentId) ?? item : item);
        }
        session.fingerprint = planFingerprint(state, session.patch);
      }
      break;
    }
    case "property.save": {
      const property = required<Property>(payload, "property");
      if (!property.name.trim()) throw new Error("Enter a property name.");
      new Intl.DateTimeFormat("en", { timeZone: property.timeZone }).format(
        new Date(),
      );
      state.properties = put(state.properties, property);
      regenerate = true;
      break;
    }
    case "service.save": {
      const service = required<ServiceDefinition>(payload, "service");
      if (
        !service.name.trim() ||
        service.estimateMinutes <= 0 ||
        service.crewSize < 1
      )
        throw new Error(
          "Enter a service name, positive duration and crew size.",
        );
      state.services = put(state.services, service);
      regenerate = true;
      break;
    }
    case "unit.save": {
      const unit = required<Unit>(payload, "unit");
      if (
        !unit.name.trim() ||
        !state.properties.some((item) => item.id === unit.propertyId) ||
        !Number.isSafeInteger(unit.standardMinutes) ||
        unit.standardMinutes < 1
      )
        throw new Error(
          "Enter a unit name, property and positive cleaning standard.",
        );
      const old = state.units.find((item) => item.id === unit.id);
      state.units = put(state.units, {
        ...unit,
        revision: (old?.revision ?? 0) + 1,
        accessUpdatedAt:
          !old || old.access !== unit.access || payload.confirmAccess === true
            ? envelope.at!
            : (old.accessUpdatedAt ?? old.factUpdatedAt),
        factUpdatedAt: envelope.at!,
        factSource: envelope.actor!,
      });
      for (const field of [
        "access",
        "cleanliness",
        "permissionGranted",
        "standardMinutes",
        "tags",
      ] as const)
        if (!old || JSON.stringify(old[field]) !== JSON.stringify(unit[field]))
          state.facts.push({
            id: stableId("fact", envelope.id, unit.id, field),
            entityType: "unit",
            entityId: unit.id,
            key: field,
            value: unit[field] ?? null,
            source: envelope.actor!,
            observedAt: envelope.at!,
            recordedAt: envelope.at!,
            revision: (old?.revision ?? 0) + 1,
          });
      if (old && old.propertyId !== unit.propertyId)
        for (const shift of state.shifts.filter((item) =>
          ["Planned", "Active"].includes(item.status),
        ))
          for (const assignmentId of shift.assignmentIds.filter(
            (aid) =>
              state.assignments.find((a) => a.id === aid)?.unitId === unit.id,
          ))
            state.impacts.push({
              id: stableId(
                "property-move",
                assignmentId,
                shift.id,
                envelope.id,
              ),
              shiftId: shift.id,
              propertyId: shift.propertyId,
              date: shift.date,
              subjectType: "assignment",
              subjectId: assignmentId,
              assignmentId,
              title: "Unit moved to another property",
              before: old.propertyId,
              after: unit.propertyId,
              action: "release-work",
              status: "pending",
              severity: "blocker",
              createdAt: envelope.at!,
              sourceRevision: state.revision + 1,
            });
      regenerate = true;
      break;
    }
    case "unit.archive":
      state.units = state.units.map((item) =>
        item.id === id("unitId") ? { ...item, archived: true } : item,
      );
      regenerate = true;
      break;
    case "person.save": {
      const person = required<Person>(payload, "person");
      if (!person.name.trim()) throw new Error("Enter the person’s name.");
      const previous=state.people.find(item=>item.id===person.id);
      if(person.requiresBuddy!==undefined&&typeof person.requiresBuddy!=='boolean')throw new Error('Choose whether this person requires a buddy.');
      if(person.requiresBuddy){
        person.buddyRestriction??=previous?.requiresBuddy?previous.buddyRestriction:{from:envelope.at!};
        const restriction=person.buddyRestriction;
        if(restriction&&(!instantOK(restriction.from)||restriction.until!==undefined&&(!instantOK(restriction.until)||Date.parse(restriction.until)<=Date.parse(restriction.from))))throw new Error('Choose a valid effective buddy-restriction interval.');
        if(restriction)person.buddyRestriction={from:new Date(restriction.from).toISOString(),until:restriction.until?new Date(restriction.until).toISOString():undefined};
      }else person.buddyRestriction=undefined;
      if (
        Object.entries(person.externalIds).some(
          ([source, external]) =>
            external &&
            state.people.some(
              (other) =>
                other.id !== person.id &&
                other.externalIds[source] === external,
            ),
        )
      )
        throw new Error(
          "This external employee identifier belongs to another person. Resolve the mapping first.",
        );
      state.people = put(state.people, {
        ...person,
        skills: [
          ...new Set(person.skills.map((item) => item.trim()).filter(Boolean)),
        ],
        revision:
          (state.people.find((item) => item.id === person.id)?.revision ?? 0) +
          1,
      });
      if(JSON.stringify([previous?.requiresBuddy,previous?.buddyRestriction])!==JSON.stringify([person.requiresBuddy,person.buddyRestriction]))state.facts.push({id:stableId('buddy-policy',envelope.id,person.id),entityType:'person',entityId:person.id,key:'buddy-restriction',value:{previous:{requiresBuddy:previous?.requiresBuddy,restriction:previous?.buddyRestriction},current:{requiresBuddy:person.requiresBuddy,restriction:person.buddyRestriction}},source:envelope.actor!,observedAt:envelope.at!,recordedAt:envelope.at!,revision:(previous?.revision??0)+1});
      break;
    }
    case "person.archive":
      state.people = state.people.map((item) =>
        item.id === id("personId") ? { ...item, archived: true } : item,
      );
      break;
    case "booking.save": {
      const booking = required<BookingSegment>(payload, "booking");
      if (
        !state.units.some(
          (item) => item.id === booking.unitId && !item.archived,
        ) ||
        !booking.guest.trim()
      )
        throw new Error("Choose an existing unit and enter a booking name.");
      ensureInterval(booking.arrival, booking.departure);
      const old = state.bookings.find((item) => item.id === booking.id);
      requireRecordRevision(booking, old);
      if (
        old?.sourceUpdatedAt &&
        booking.sourceUpdatedAt &&
        booking.sourceUpdatedAt < old.sourceUpdatedAt
      )
        throw new Error(
          "This source revision is older than the current booking.",
        );
      state.bookings = put(state.bookings, {
        ...booking,
        revision: (old?.revision ?? 0) + 1,
      });
      state = revalidateStays(state);
      regenerate = true;
      break;
    }
    case "booking.cancel":
      state.bookings = state.bookings.map((item) =>
        item.id === id("bookingId")
          ? { ...item, status: "cancelled", revision: item.revision + 1 }
          : item,
      );
      state = revalidateStays(state);
      regenerate = true;
      break;
    case "stay.link": {
      const selected = new Set(required<string[]>(payload, "bookingIds"));
      const expanded = state.stays
        .filter((stay) => stay.bookingIds.some((bid) => selected.has(bid)))
        .flatMap((stay) => stay.bookingIds);
      expanded.forEach((bid) => selected.add(bid));
      const bookings = state.bookings
        .filter((item) => selected.has(item.id))
        .sort((a, b) => a.arrival.localeCompare(b.arrival));
      if (
        bookings.length !== selected.size ||
        bookings.length < 2 ||
        bookings.some(
          (item) =>
            item.unitId !== bookings[0].unitId || item.status !== "confirmed",
        )
      )
        throw new Error(
          "Choose at least two confirmed bookings in the same unit.",
        );
      const timeZone = state.properties.find(
        (item) =>
          item.id ===
          state.units.find((unit) => unit.id === bookings[0].unitId)
            ?.propertyId,
      )?.timeZone;
      if (
        bookings.some(
          (item, index) =>
            index > 0 &&
            localDate(bookings[index - 1].departure, timeZone) !==
              localDate(item.arrival, timeZone),
        )
      )
        throw new Error(
          "Continuous-stay segments must meet on consecutive booking boundaries.",
        );
      const strongIdentity =
        Boolean(bookings[0].guestRef) &&
        bookings.every((item) => item.guestRef === bookings[0].guestRef);
      if (
        !strongIdentity &&
        (payload.confirmed !== true || !String(payload.reason ?? "").trim())
      )
        throw new Error(
          "Confirm this is the same continuous stay and record a reason. Matching names alone are not sufficient.",
        );
      const stayId = stableId(
        "stay",
        ...bookings.map((item) => item.id).sort(),
      );
      state.stays = [
        ...state.stays.filter(
          (stay) => !stay.bookingIds.some((bid) => selected.has(bid)),
        ),
        {
          id: stayId,
          bookingIds: bookings.map((item) => item.id),
          confirmedBy: envelope.actor!,
          confirmedAt: envelope.at!,
          reason: String(
            payload.reason || "Verified external guest/stay identity",
          ),
          valid: true,
        },
      ];
      state.bookings = state.bookings.map((item) =>
        selected.has(item.id)
          ? { ...item, continuityId: stayId, revision: item.revision + 1 }
          : item,
      );
      regenerate = true;
      break;
    }
    case "stay.unlink": {
      const stay = state.stays.find((item) => item.id === id("stayId"));
      if (!stay) throw new Error("Continuous stay not found.");
      state.stays = state.stays.filter((item) => item.id !== stay.id);
      state.bookings = state.bookings.map((item) =>
        stay.bookingIds.includes(item.id)
          ? { ...item, continuityId: undefined, revision: item.revision + 1 }
          : item,
      );
      regenerate = true;
      break;
    }
    case "rule.save-draft": {
      const rule = required<RuleDefinition>(payload, "rule");
      state.ruleDrafts = put(state.ruleDrafts, { ...rule, status: "draft" });
      break;
    }
    case "rule.activate": {
      const rule = required<RuleDefinition>(payload, "rule");
      if (
        payload.baseRevision !== undefined &&
        payload.baseRevision !== state.revision
      )
        throw new Error(
          "This rule preview is stale. Refresh it before activation.",
        );
      validateRule(rule, state);
      state = workspaceWithRule(state, {
        ...rule,
        status: rule.status === "retired" ? "retired" : "active",
      });
      if (payload.from !== undefined || payload.to !== undefined) {
        const from = id("from");
        const to = id("to");
        if (!validDate(from) || !validDate(to) || to < from)
          throw new Error("Choose a valid preview period.");
        const baseRange = rangeFor(state, envelope.at);
        regenerationRange = {
          from: from < baseRange.from ? from : baseRange.from,
          to: to > baseRange.to ? to : baseRange.to,
        };
      }
      state.ruleDrafts = state.ruleDrafts.filter((item) => item.id !== rule.id);
      regenerate = true;
      break;
    }
    case "rule.disable-unit": {
      const unit = state.units.find((item) => item.id === id("unitId"));
      const rule = state.rules.find((item) => item.id === id("ruleId"));
      if (
        !unit ||
        !rule ||
        rule.scope !== "property" ||
        rule.scopeId !== unit.propertyId
      )
        throw new Error("Choose an inherited property rule for this unit.");
      if (payload.disabled && !String(payload.reason ?? "").trim())
        throw new Error("Record why the inherited rule is disabled here.");
      unit.disabledInheritedRuleIds = payload.disabled
        ? [...new Set([...unit.disabledInheritedRuleIds, rule.id])]
        : unit.disabledInheritedRuleIds.filter((rid) => rid !== rule.id);
      unit.disabledRuleReasons = {
        ...unit.disabledRuleReasons,
        [rule.id]: String(payload.reason ?? ""),
      };
      regenerate = true;
      break;
    }
    case "assignment.save": {
      const assignment = required<Assignment>(payload, "assignment");
      validateAssignment(state, assignment);
      const old = state.assignments.find((item) => item.id === assignment.id);
      if(['stayCycleId','stayCycleRevision','followsAssignmentId'].some(key=>assignment[key as keyof Assignment]!==old?.[key as keyof Assignment]))throw new Error('Use the reservation-cycle action to link work or register a guest-return follow-up.');
      requireRecordRevision(assignment, old);
      if (old && ["in-progress", "completed"].includes(old.status))
        throw new Error(
          "Actual work cannot be rewritten. Pause and schedule the remainder or create rework.",
        );
      if (assignment.status !== (old?.status ?? "open"))
        throw new Error(
          "Use the assignment lifecycle action to change its status.",
        );
      if (
        old
          ? assignment.origin !== old.origin ||
            assignment.contextKey !== old.contextKey ||
            JSON.stringify(assignment.occurrenceIds) !==
              JSON.stringify(old.occurrenceIds) ||
            assignment.completedAt !== old.completedAt ||
            assignment.completionResult !== old.completionResult
          : assignment.origin !== "manual" ||
            assignment.occurrenceIds.length ||
            assignment.completedAt !== undefined ||
            assignment.completionResult !== undefined
      )
        throw new Error(
          "Requirement provenance and actual completion facts cannot be edited through assignment details.",
        );
      if (
        "actualSessions" in assignment ||
        "sessions" in assignment ||
        (old?.occurrenceIds.length &&
          JSON.stringify(old.dependsOn) !==
            JSON.stringify(assignment.dependsOn))
      )
        throw new Error(
          "Requirement provenance and actual completion facts cannot be edited through assignment details.",
        );
      if (
        old?.occurrenceIds.length &&
        (assignment.unitId !== old.unitId ||
          assignment.serviceId !== old.serviceId)
      )
        throw new Error(
          "Rule requirements retain their unit and service. Create separate manual work for a different obligation.",
        );
      const manualOverrides = { ...old?.manualOverrides };
      const explicit=old&&assignment.estimateMinutes!==old.estimateMinutes&&(!assignment.estimateSource||assignment.estimateSource===old.estimateSource)?'fixed':assignment.estimateSource??old?.estimateSource??'fixed';
      const estimateAssignment=old?.estimateProvenance?.confidence==='unrecorded'&&assignment.estimateMinutes===old.estimateMinutes&&explicit===old.estimateSource?{...assignment,estimateProvenance:old.estimateProvenance}:assignment;
      const estimate=resolveEstimate(state,{unitId:assignment.unitId,serviceId:assignment.serviceId,assignment:estimateAssignment,source:explicit});
      assignment.estimateSource=estimate.source;assignment.estimateMinutes=estimate.minutes;assignment.estimateProvenance=estimate;
      if(old?.occurrenceIds.length){manualOverrides.estimateSource=estimate.source;if(estimate.source==='fixed')manualOverrides.estimateMinutes=estimate.minutes;else delete manualOverrides.estimateMinutes;}
      if (old?.occurrenceIds.length)
        for (const field of [
          "earliestStart",
          "dueAt",
          "estimateMinutes",
          "requiredSkills",
          "preferredSkills",
          "crewSize",
          "priority",
          "notes",
        ] as const)
          if (!(field==='estimateMinutes'&&estimate.source!=='fixed') && JSON.stringify(old[field]) !== JSON.stringify(assignment[field]))
            Object.assign(manualOverrides, {
              [field]: assignment[field] ?? null,
            });
      if(!old&&serviceFor(state,assignment.serviceId)?.kind==='inspection'){
        const prerequisite=state.assignments.find(item=>assignment.dependsOn?.includes(item.id)&&item.stayCycleId);
        if(prerequisite){assignment.stayCycleId=prerequisite.stayCycleId;assignment.stayCycleRevision=prerequisite.stayCycleRevision;}
      }
      state.assignments = put(state.assignments, {
        ...assignment,
        manualOverrides: Object.keys(manualOverrides).length
          ? manualOverrides
          : undefined,
        revision: (old?.revision ?? 0) + 1,
      });
      break;
    }
    case "assignment.move": {
      const assignment = assignmentById();
      const date = id("date");
      if (!validDate(date) || date === assignment.date)
        throw new Error("Choose a different valid workday.");
      if (
        assignment.status === "in-progress" ||
        assignment.status === "completed"
      )
        throw new Error(
          "Pause active work before scheduling the remainder; completed work cannot be moved.",
        );
      assignment.date = date;
      assignment.manualDate = true;
      assignment.revision++;
      assignment.notes = [assignment.notes, String(payload.reason ?? "")]
        .filter(Boolean)
        .join("\n");
      state.allocations = state.allocations.map((item) =>
        item.assignmentId === assignment.id &&
        ["committed", "tentative"].includes(item.status)
          ? { ...item, status: "released" }
          : item,
      );
      state.shifts = state.shifts.map((item) =>
        !["Completed", "Cancelled"].includes(item.status) &&
        item.assignmentIds.includes(assignment.id)
          ? {
              ...item,
              assignmentIds: item.assignmentIds.filter(
                (aid) => aid !== assignment.id,
              ),
              revision: item.revision + 1,
            }
          : item,
      );
      break;
    }
    case "assignment.decline": {
      const assignment = assignmentById();
      if (
        assignment.status === "in-progress" ||
        assignment.status === "completed"
      )
        throw new Error(
          "Started or completed work must retain its actual history. Pause active work first.",
        );
      if (!String(payload.reason ?? "").trim())
        throw new Error("Record a reason for declining this occurrence.");
      assignment.status = "declined";
      assignment.declineReason = String(payload.reason);
      assignment.revision++;
      state.occurrences = state.occurrences.map((item) =>
        assignment.occurrenceIds.includes(item.id)
          ? { ...item, status: "suppressed" }
          : item,
      );
      state.allocations = state.allocations.map((item) =>
        item.assignmentId === assignment.id &&
        ["committed", "tentative"].includes(item.status)
          ? { ...item, status: "released" }
          : item,
      );
      state.shifts = state.shifts.map((item) =>
        !["Completed", "Cancelled"].includes(item.status) &&
        item.assignmentIds.includes(assignment.id)
          ? {
              ...item,
              assignmentIds: item.assignmentIds.filter(
                (aid) => aid !== assignment.id,
              ),
              revision: item.revision + 1,
            }
          : item,
      );
      break;
    }
    case "assignment.restore": {
      const assignment = assignmentById();
      if (!["declined", "cancelled"].includes(assignment.status))
        throw new Error("Only declined or cancelled work can be restored.");
      assignment.status = "open";
      assignment.revision++;
      assignment.declineReason = undefined;
      state.occurrences = state.occurrences.map((item) =>
        assignment.occurrenceIds.includes(item.id)
          ? { ...item, status: "pending" }
          : item,
      );
      break;
    }
    case "assignment.bulk": {
      const assignmentIds = [
        ...new Set(required<string[]>(payload, "assignmentIds")),
      ];
      const assignments = assignmentIds.map((aid) =>
        state.assignments.find((item) => item.id === aid),
      );
      if (!assignments.length || assignments.some((item) => !item))
        throw new Error("Choose existing assignments for the bulk action.");
      const action = id("action");
      if (action === "shift") {
        const shift = state.shifts.find((item) => item.id === id("shiftId"));
        if (!shift || !["Draft", "Planned", "Active"].includes(shift.status))
          throw new Error("Choose a draft, planned or active shift.");
        for (const assignment of assignments as Assignment[]) {
          if (
            ["completed", "declined", "cancelled", "in-progress"].includes(
              assignment.status,
            )
          )
            throw new Error(
              "Only open or paused work can be added to this plan.",
            );
          if (
            state.units.find((item) => item.id === assignment.unitId)
              ?.propertyId !== shift.propertyId ||
            (assignment.date && assignment.date !== shift.date)
          )
            throw new Error(
              "All selected work must match the shift property and workday. Move it explicitly first.",
            );
          if (!assignment.date) {
            assignment.date = shift.date;
            assignment.manualDate = true;
          }
        }
        shift.assignmentIds = [
          ...new Set([...shift.assignmentIds, ...assignmentIds]),
        ];
        shift.revision++;
        if (shift.status !== "Draft") {
          validatePlan(state, shift);
          state = syncAllocations(state, shift);
        }
      } else if (action === "move" || action === "decline") {
        if (action === "move" && !validDate(String(payload.date ?? "")))
          throw new Error("Choose a valid destination workday.");
        if (action === "decline" && !String(payload.reason ?? "").trim())
          throw new Error("Record a reason for declining these occurrences.");
        for (const assignment of assignments as Assignment[]) {
          if (["in-progress", "completed"].includes(assignment.status))
            throw new Error(
              "Active and completed work cannot be changed by this bulk action.",
            );
          if (action === "move") {
            assignment.date = String(payload.date);
            assignment.manualDate = true;
            assignment.notes = [assignment.notes, String(payload.reason ?? "")]
              .filter(Boolean)
              .join("\n");
          } else {
            assignment.status = "declined";
            assignment.declineReason = String(payload.reason);
            state.occurrences = state.occurrences.map((item) =>
              assignment.occurrenceIds.includes(item.id)
                ? { ...item, status: "suppressed" }
                : item,
            );
          }
          assignment.revision++;
        }
        state.allocations = state.allocations.map((item) =>
          assignmentIds.includes(item.assignmentId) &&
          ["committed", "tentative"].includes(item.status)
            ? { ...item, status: "released" }
            : item,
        );
        state.shifts = state.shifts.map((item) =>
          !["Completed", "Cancelled"].includes(item.status) &&
          item.assignmentIds.some((aid) => assignmentIds.includes(aid))
            ? {
                ...item,
                assignmentIds: item.assignmentIds.filter(
                  (aid) => !assignmentIds.includes(aid),
                ),
                revision: item.revision + 1,
              }
            : item,
        );
      } else throw new Error("Choose a supported bulk action.");
      break;
    }
    case "availability.save": {
      const personId = id("personId");
      const businessDate = id("businessDate");
      const intervals = required<AvailabilityInterval[]>(payload, "intervals");
      if (
        !state.people.some((item) => item.id === personId && !item.archived) ||
        !validDate(businessDate)
      )
        throw new Error("Choose an existing person and a valid workday.");
      if (
        intervals.some(
          (item) =>
            item.personId !== personId || item.businessDate !== businessDate,
        )
      )
        throw new Error(
          "Every interval must belong to the selected person and business date.",
        );
      state.availability = [
        ...state.availability.filter(
          (item) =>
            item.personId !== personId || item.businessDate !== businessDate,
        ),
        ...intervals,
      ];
      validateAvailability(state);
      break;
    }
    case "availability.remove":
      state.availability = state.availability.filter(
        (item) => item.id !== id("intervalId"),
      );
      break;
    case "availability.copy": {
      const sourceDate = id("sourceDate");
      const from = id("from");
      const to = id("to");
      const weekdays = required<number[]>(payload, "weekdays");
      const personIds = required<string[]>(payload, "personIds");
      const timeZone = String(payload.timeZone ?? "Europe/Helsinki");
      if (
        !validDate(from) ||
        !validDate(to) ||
        to < from ||
        durationMinutes(`${from}T00:00:00Z`, `${to}T00:00:00Z`) > 366 * 1440
      )
        throw new Error("Choose a valid copy period no longer than one year.");
      const source = state.availability.filter(
        (item) =>
          item.businessDate === sourceDate && personIds.includes(item.personId),
      );
      for (let date = from; date <= to; date = addDays(date, 1)) {
        if (
          date === sourceDate ||
          (weekdays.length &&
            !weekdays.includes(new Date(`${date}T12:00:00Z`).getUTCDay()))
        )
          continue;
        for (const personId of personIds) {
          if (
            !payload.replace &&
            state.availability.some(
              (item) =>
                item.personId === personId && item.businessDate === date,
            )
          )
            continue;
          const rows = source
            .filter((item) => item.personId === personId)
            .map((item) => ({
              ...item,
              id: stableId("copy-day", personId, date, item.id),
              businessDate: date,
              start: localToInstant(
                date,
                localTime(item.start, timeZone),
                timeZone,
                localDate(item.start, timeZone) > sourceDate,
              ),
              end: localToInstant(
                date,
                localTime(item.end, timeZone),
                timeZone,
                localDate(item.end, timeZone) > sourceDate,
              ),
              source: "manual" as const,
              localOverride: true,
            }));
          state.availability = [
            ...state.availability.filter(
              (item) =>
                item.personId !== personId || item.businessDate !== date,
            ),
            ...rows,
          ];
        }
      }
      validateAvailability(state);
      break;
    }
    case "attendance.record": {
      const supplied = payload.event as AttendanceEvent | undefined;
      const event: AttendanceEvent = supplied ?? {
        id: stableId("attendance", envelope.id),
        personId: id("personId"),
        shiftId: payload.shiftId as string | undefined,
        status: required<AttendanceEvent["status"]>(payload, "status"),
        at: envelope.at!,
      };
      if (!state.people.some((item) => item.id === event.personId))
        throw new Error("Choose an existing person.");
      state.attendance = put(state.attendance, event);
      break;
    }
    case "shift.save": {
      const shift = required<Shift>(payload, "shift");
      const old = state.shifts.find((item) => item.id === shift.id);
      requireRecordRevision(shift, old);
      if (old?.status === "Completed" || old?.status === "Cancelled")
        throw new Error(
          "Closed shifts are read-only; append corrections through history.",
        );
      if (shift.status !== (old?.status ?? "Draft"))
        throw new Error("Use the shift lifecycle action to change its status.");
      if (
        shift.startedAt !== old?.startedAt ||
        shift.completedAt !== old?.completedAt ||
        shift.cancelledAt !== old?.cancelledAt ||
        shift.cancellationReason !== old?.cancellationReason
      )
        throw new Error(
          "Shift execution and cancellation facts are recorded through lifecycle actions.",
        );
      if (
        old &&
        state.sessions.some((session) =>
          old.assignmentIds.includes(session.assignmentId),
        ) &&
        (old.date !== shift.date ||
          old.propertyId !== shift.propertyId ||
          old.start !== shift.start ||
          old.end !== shift.end)
      )
        throw new Error(
          "A shift containing actual work must retain its date and time boundaries. Move the remaining work through a continuation.",
        );
      if (payload.availability) {
        state.availability = [
          ...state.availability,
          ...(payload.availability as AvailabilityInterval[]).filter(
            (item) => !state.availability.some((old) => old.id === item.id),
          ),
        ];
        validateAvailability(state);
      }
      const next = { ...shift, revision: (old?.revision ?? 0) + 1 };
      state.shifts = put(state.shifts, next);
      if (next.status === "Planned" || next.status === "Active") {
        validatePlan(state, next);
        state = syncAllocations(state, next);
      }
      break;
    }
    case "shift.plan": {
      const shift = shiftById();
      validatePlan(state, shift);
      if (
        shift.status === "Active" ||
        shift.status === "Completed" ||
        shift.status === "Cancelled"
      )
        throw new Error("Only a draft or planned shift can be planned.");
      shift.status = "Planned";
      shift.revision++;
      state = syncAllocations(state, shift);
      break;
    }
    case "shift.start": {
      const shift = shiftById();
      if (shift.status !== "Planned")
        throw new Error("Plan this shift before starting it.");
      validatePlan(state, shift);
      if (!shift.positions.some((item) => item.personId))
        throw new Error(
          "Include at least one person before starting the shift.",
        );
      shift.status = "Active";
      shift.startedAt = envelope.at!;
      shift.revision++;
      break;
    }
    case "shift.cancel": {
      const shift = shiftById();
      if (shift.status === "Completed")
        throw new Error("A completed shift cannot be cancelled.");
      if (shift.status === "Cancelled")
        throw new Error("This shift is already cancelled.");
      const reason = String(payload.reason ?? "").trim();
      if (!reason) throw new Error("Record a reason for cancelling the shift.");
      if (
        state.sessions.some(
          (session) =>
            !session.end &&
            state.allocations.find((item) => item.id === session.allocationId)
              ?.shiftId === shift.id,
        )
      )
        throw new Error("Pause active work before cancelling the shift.");
      if (shift.status === "Active")
        for (const assignment of state.assignments.filter(
          (item) =>
            shift.assignmentIds.includes(item.id) &&
            !["completed", "declined", "cancelled"].includes(item.status),
        ))
          state.handovers = [
            ...(state.handovers ?? []),
            {
              id: stableId("handover", shift.id, assignment.id),
              assignmentId: assignment.id,
              fromShiftId: shift.id,
              ownerLabel: envelope.actor!,
              dueAt: assignment.dueAt,
              notes: reason,
              status: "open",
            },
          ];
      shift.status = "Cancelled";
      shift.cancelledAt = envelope.at!;
      shift.cancellationReason = reason;
      shift.revision++;
      state.allocations = state.allocations.map((item) =>
        item.shiftId === shift.id &&
        ["committed", "tentative"].includes(item.status)
          ? {
              ...item,
              status:
                state.assignments.find(
                  (assignment) => assignment.id === item.assignmentId,
                )?.status === "completed"
                  ? "finished"
                  : "released",
              revision: item.revision + 1,
            }
          : item,
      );
      break;
    }
    case "shift.scope": {
      const shift = shiftById();
      const date = id("date");
      const propertyId = id("propertyId");
      const start = id("start");
      const end = id("end");
      if (shift.status === "Completed" || shift.status === "Cancelled")
        throw new Error("Closed shift scope cannot change.");
      ensureInterval(start, end);
      if (
        !validDate(date) ||
        !state.properties.some((item) => item.id === propertyId)
      )
        throw new Error("Choose a valid scope.");
      const protectedIds = shift.assignmentIds.filter(
        (aid) =>
          state.sessions.some((item) => item.assignmentId === aid) ||
          ["in-progress", "completed"].includes(
            state.assignments.find((item) => item.id === aid)?.status ?? "",
          ),
      );
      if (propertyId !== shift.propertyId && protectedIds.length)
        throw new Error(
          "A shift containing actual work cannot move to another property.",
        );
      if (
        protectedIds.length &&
        (date !== shift.date || start !== shift.start || end !== shift.end)
      )
        throw new Error(
          "A shift containing actual work must retain its date and time boundaries. Move the remaining work through a continuation.",
        );
      const retained: string[] = [];
      for (const aid of shift.assignmentIds) {
        const assignment = state.assignments.find((item) => item.id === aid)!;
        const propertyMatches =
          state.units.find((unit) => unit.id === assignment.unitId)
            ?.propertyId === propertyId;
        if (protectedIds.includes(aid)) {
          retained.push(aid);
          continue;
        }
        if (payload.moveAssignments && propertyMatches) {
          assignment.date = date;
          assignment.manualDate = true;
          assignment.revision++;
          retained.push(aid);
        } else if (propertyMatches && assignment.date === date)
          retained.push(aid);
      }
      const offset = Date.parse(start) - Date.parse(shift.start);
      state.allocations = state.allocations.map((allocation) => {
        if (
          allocation.shiftId !== shift.id ||
          !["tentative", "committed"].includes(allocation.status)
        )
          return allocation;
        if (!retained.includes(allocation.assignmentId))
          return {
            ...allocation,
            status: "released",
            revision: allocation.revision + 1,
          };
        if (
          !allocation.start ||
          !allocation.end ||
          protectedIds.includes(allocation.assignmentId)
        )
          return allocation;
        const nextStart = new Date(
          Date.parse(allocation.start) + offset,
        ).toISOString();
        const nextEnd = new Date(
          Date.parse(allocation.end) + offset,
        ).toISOString();
        return {
          ...allocation,
          start: nextStart >= start && nextEnd <= end ? nextStart : undefined,
          end: nextStart >= start && nextEnd <= end ? nextEnd : undefined,
          revision: allocation.revision + 1,
        };
      });
      shift.positions = shift.positions.map((position) => ({
        ...position,
        coverageStart: new Date(
          Math.max(
            Date.parse(start),
            Math.min(
              Date.parse(end) - 60000,
              Date.parse(position.coverageStart) + offset,
            ),
          ),
        ).toISOString(),
        coverageEnd: new Date(
          Math.min(
            Date.parse(end),
            Math.max(
              Date.parse(start) + 60000,
              Date.parse(position.coverageEnd) + offset,
            ),
          ),
        ).toISOString(),
      }));
      shift.propertyId = propertyId;
      shift.date = date;
      shift.start = start;
      shift.end = end;
      shift.assignmentIds = retained;
      shift.revision++;
      if (shift.status !== "Draft") {
        validatePlan(state, shift);
        state = syncAllocations(state, shift);
      }
      break;
    }
    case "shift.close": {
      const shift = shiftById();
      if (shift.status !== "Active")
        throw new Error("Only an active shift can be completed.");
      if (
        state.sessions.some(
          (session) =>
            !session.end &&
            state.allocations.find((item) => item.id === session.allocationId)
              ?.shiftId === shift.id,
        )
      )
        throw new Error(
          "Pause or complete all active work before closing the shift.",
        );
      const resolutions = (payload.resolutions ?? {}) as Record<string, string>;
      const handoverOwners = (payload.handoverOwners ?? {}) as Record<
        string,
        string
      >;
      for (const assignment of state.assignments.filter(
        (item) =>
          shift.assignmentIds.includes(item.id) &&
          !["completed", "declined", "cancelled"].includes(item.status),
      )) {
        const resolution = resolutions[assignment.id];
        if (
          !["pool", "decline", "continue"].includes(resolution) &&
          !resolution?.startsWith("transfer:")
        )
          throw new Error("Choose an outcome for every unfinished assignment.");
        if (resolutions[assignment.id] === "decline") {
          if (!String(payload.reason ?? "").trim())
            throw new Error("Record a reason for declining unfinished work.");
          assignment.status = "declined";
          assignment.declineReason = String(payload.reason);
          state.occurrences = state.occurrences.map((item) =>
            assignment.occurrenceIds.includes(item.id)
              ? { ...item, status: "suppressed" }
              : item,
          );
        } else if (resolutions[assignment.id] === "continue") {
          assignment.status = "paused";
          assignment.remainingMinutes = Math.max(
            0,
            assignment.estimateMinutes -
              state.sessions
                .filter(
                  (item) => item.assignmentId === assignment.id && item.end,
                )
                .reduce(
                  (sum, item) => sum + durationMinutes(item.start, item.end!),
                  0,
                ),
          );
        }
        if (resolution.startsWith("transfer:")) {
          const target = state.shifts.find(
            (item) => item.id === resolution.slice(9),
          );
          if (
            !target ||
            target.id === shift.id ||
            target.propertyId !== shift.propertyId ||
            !["Draft", "Planned"].includes(target.status)
          )
            throw new Error(
              "Choose another draft or planned shift at the same property for transfer.",
            );
          assignment.date = target.date;
          assignment.manualDate = true;
          target.assignmentIds = [
            ...new Set([...target.assignmentIds, assignment.id]),
          ];
          target.revision++;
        }
        if (
          handoverOwners[assignment.id] &&
          !state.people.some(
            (person) =>
              person.id === handoverOwners[assignment.id] && !person.archived,
          )
        )
          throw new Error("Choose an available person to own the handover.");
        if (resolution !== "decline")
          state.handovers = [
            ...(state.handovers ?? []),
            {
              id: stableId("handover", shift.id, assignment.id),
              assignmentId: assignment.id,
              fromShiftId: shift.id,
              ownerId: handoverOwners[assignment.id] || undefined,
              ownerLabel: handoverOwners[assignment.id]
                ? state.people.find(
                    (person) => person.id === handoverOwners[assignment.id],
                  )!.name
                : envelope.actor!,
              dueAt: assignment.dueAt,
              notes: String(
                payload.reason ??
                  "Unfinished work retained after shift closure.",
              ),
              status: "open",
            },
          ];
        assignment.revision++;
      }
      state.allocations = state.allocations.map((item) =>
        item.shiftId === shift.id && item.status === "committed"
          ? {
              ...item,
              status:
                state.assignments.find(
                  (assignment) => assignment.id === item.assignmentId,
                )?.status === "completed"
                  ? "finished"
                  : "released",
            }
          : item,
      );
      shift.status = "Completed";
      shift.completedAt = envelope.at!;
      shift.revision++;
      const targets = [
        ...new Set(
          Object.values(resolutions)
            .filter((value) => value.startsWith("transfer:"))
            .map((value) => value.slice(9)),
        ),
      ];
      for (const targetId of targets) {
        const target = state.shifts.find((item) => item.id === targetId)!;
        if (target.status === "Planned") {
          validatePlan(state, target);
          state = syncAllocations(state, target);
        }
      }
      break;
    }
    case "allocation.schedule": {
      let allocation = required<AssignmentAllocation>(payload, "allocation");
      const shift = state.shifts.find((item) => item.id === allocation.shiftId);
      const assignment = state.assignments.find(
        (item) => item.id === allocation.assignmentId,
      );
      if (
        !shift ||
        !assignment ||
        !["Draft", "Planned", "Active"].includes(shift.status)
      )
        throw new Error(
          "Choose an existing draft, planned or active shift and assignment.",
        );
      if (!shift.assignmentIds.includes(assignment.id))
        throw new Error("Include this work in the shift before scheduling it.");
      const owned = state.allocations.find(item => item.id === allocation.id);
      if (owned && (owned.shiftId !== allocation.shiftId || owned.assignmentId !== allocation.assignmentId || ['finished', 'released'].includes(owned.status))) throw new Error('Historical allocations and their ownership cannot be overwritten.');
      if (owned) requireRecordRevision(allocation, owned);
      if (
        ["completed", "in-progress", "declined", "cancelled"].includes(
          assignment.status,
        )
      )
        throw new Error("Only unstarted or paused work can be scheduled.");
      if (
        state.sessions.some(
          (item) => item.assignmentId === assignment.id && !item.end,
        )
      )
        throw new Error("Pause active work before changing its schedule.");
      if (allocation.start && allocation.end) {
        ensureInterval(allocation.start, allocation.end);
        if (allocation.start < shift.start || allocation.end > shift.end)
          throw new Error("Schedule this work inside the shift interval.");
      }
      if (
        allocation.participantIds.some(
          (personId) =>
            !shift.positions.some((item) => item.personId === personId),
        )
      )
        throw new Error("Include every participant in the shift first.");
      const candidate = basePlanPatch(state, shift);
      candidate.allocations = [...candidate.allocations.filter(item => item.assignmentId !== assignment.id), { id: allocation.id, assignmentId: allocation.assignmentId, shiftId: allocation.shiftId, start: allocation.start, end: allocation.end, participantIds: allocation.participantIds, revision: allocation.revision }];
      const assessment = assessPlan(state, candidate, { at: envelope.at! });
      if (!assessment.canApply) throw new Error(assessment.structuralBlockers[0].message);
      if (owned && state.sessions.some(item => item.allocationId === owned.id) && JSON.stringify([owned.start, owned.end, owned.participantIds]) !== JSON.stringify([allocation.start, allocation.end, allocation.participantIds])) { owned.status = 'released'; owned.revision++; allocation = { ...allocation, id: stableId('continuation', envelope.id, owned.id), revision: 0 }; }
      state.allocations = state.allocations.filter(
        (item) =>
          !(
            item.assignmentId === allocation.assignmentId &&
            item.shiftId === allocation.shiftId &&
            ["committed", "tentative"].includes(item.status) &&
            item.id !== allocation.id
          ),
      );
      state.allocations = put(state.allocations, {
        ...allocation,
        status: shift.status === "Draft" ? "tentative" : "committed",
        revision:
          (state.allocations.find((item) => item.id === allocation.id)
            ?.revision ?? 0) + 1,
      });
      const structural = assignmentConflicts(state, assignment.id).find(
        (item) => ["overlap", "availability"].includes(item.code),
      );
      if (structural) throw new Error(structural.message);
      break;
    }
    case "allocation.release": {
      const allocation = state.allocations.find(
        (item) => item.id === id("allocationId"),
      );
      if (!allocation) throw new Error("Allocation not found.");
      if (
        allocation.status === "finished" ||
        state.shifts.some(
          (item) =>
            item.id === allocation.shiftId &&
            ["Completed", "Cancelled"].includes(item.status),
        )
      )
        throw new Error(
          "Historical allocations cannot be released. Create a new continuation for remaining work.",
        );
      if (
        state.sessions.some(
          (item) => item.allocationId === allocation.id && !item.end,
        )
      )
        throw new Error("Pause active work before releasing it.");
      allocation.status = "released";
      allocation.revision++;
      state.shifts = state.shifts.map((item) =>
        item.id === allocation.shiftId
          ? {
              ...item,
              assignmentIds: item.assignmentIds.filter(
                (aid) => aid !== allocation.assignmentId,
              ),
              revision: item.revision + 1,
            }
          : item,
      );
      break;
    }
    case "work.start": {
      const assignment = assignmentById();
      const allocation = committedAllocation(state, assignment.id);
      if (
        !allocation ||
        state.shifts.find((item) => item.id === allocation.shiftId)?.status !==
          "Active"
      )
        throw new Error("Start the committed shift before starting this work.");
      if (!["open", "paused"].includes(assignment.status))
        throw new Error("Only open or paused work can start.");
      if (payload.personIds) {
        if(!Array.isArray(payload.personIds)||payload.personIds.some(value=>typeof value!=='string'))throw new Error('Choose valid work participants.');
        allocation.participantIds = [...new Set(payload.personIds as string[])];
        allocation.revision++;
      }
      const blockers=executionConflicts(state,assignment.id,allocation.participantIds,envelope.at!);
      if(blockers.length)throw new Error(blockers[0].message);
      const window=effectiveAssignmentWindow(state,assignment);
      if(window.dueAt&&envelope.at!>window.dueAt)state.facts.push({id:stableId('lateness-start',envelope.id,assignment.id),entityType:'assignment',entityId:assignment.id,key:'late-start',value:{dueAt:window.dueAt,startedAt:envelope.at!,latenessMinutes:durationMinutes(window.dueAt,envelope.at!),occurrenceIds:[...assignment.occurrenceIds]},source:envelope.actor!,observedAt:envelope.at!,recordedAt:envelope.at!,revision:assignment.revision+1});
      assignment.status = "in-progress";
      assignment.revision++;
      state.sessions.push({
        id: stableId("session", envelope.id),
        assignmentId: assignment.id,
        allocationId: allocation.id,
        personIds: [...allocation.participantIds],
        start: envelope.at!,
      });
      break;
    }
    case 'work.join':
    case 'work.leave': {
      const assignment=assignmentById(),allocation=committedAllocation(state,assignment.id),personId=id('personId');
      if(assignment.status!=='in-progress'||!allocation||state.shifts.find(item=>item.id===allocation.shiftId)?.status!=='Active')throw new Error('Only an active task can change its current participants.');
      const participants=activeParticipants(state,assignment.id),joining=envelope.type==='work.join';
      if(joining?participants.includes(personId):!participants.includes(personId))throw new Error(joining?'This person is already participating.':'This person is not currently participating.');
      const current=state.sessions.filter(item=>item.assignmentId===assignment.id&&!item.end);
      if(current.some(item=>item.start>envelope.at!))throw new Error('Participation changes cannot precede the active session.');
      const next=joining?[...participants,personId]:participants.filter(id=>id!==personId);
      const blockers=executionConflicts(state,assignment.id,next,envelope.at!);
      if(joining&&blockers.length)throw new Error(blockers[0].message);
      for(const session of current)session.end=envelope.at!;
      if(next.length&&!blockers.length)state.sessions.push({id:stableId('crew-session',envelope.id),assignmentId:assignment.id,allocationId:allocation.id,personIds:next,start:envelope.at!});
      else {
        assignment.status='paused';
        assignment.remainingMinutes=Math.max(0,assignment.estimateMinutes-actualMinutes(state,assignment.id,envelope.at!));
      }
      assignment.revision++;
      state.facts.push({id:stableId('participation',envelope.id),entityType:'assignment',entityId:assignment.id,key:joining?'participant-joined':'participant-left',value:{personId,participants:next,paused:assignment.status==='paused',reasons:blockers.map(item=>item.message)},source:envelope.actor!,observedAt:envelope.at!,recordedAt:envelope.at!,revision:assignment.revision});
      break;
    }
    case "work.pause": {
      const assignment = assignmentById();
      if (assignment.status !== "in-progress")
        throw new Error("Only active work can pause.");
      state.sessions = state.sessions.map((item) =>
        item.assignmentId === assignment.id && !item.end
          ? { ...item, end: envelope.at! }
          : item,
      );
      assignment.status = "paused";
      assignment.revision++;
      assignment.remainingMinutes = Math.max(
        0,
        assignment.estimateMinutes - actualMinutes(state,assignment.id,envelope.at!),
      );
      break;
    }
    case "work.complete": {
      const assignment = assignmentById();
      if (assignment.status !== "in-progress")
        throw new Error("Start the work before recording its completion.");
      state.sessions = state.sessions.map((item) =>
        item.assignmentId === assignment.id && !item.end
          ? { ...item, end: envelope.at! }
          : item,
      );
      assignment.status = "completed";
      assignment.completedAt = envelope.at!;
      assignment.remainingMinutes = 0;
      assignment.revision++;
      const completionWindow = effectiveAssignmentWindow(state, assignment);
      if (completionWindow.dueAt && envelope.at! > completionWindow.dueAt) state.facts.push({ id: stableId('lateness-completion', envelope.id, assignment.id), entityType: 'assignment', entityId: assignment.id, key: 'missed-deadline', value: { dueAt: completionWindow.dueAt, completedAt: envelope.at!, latenessMinutes: durationMinutes(completionWindow.dueAt, envelope.at!), occurrenceIds: [...assignment.occurrenceIds] }, source: envelope.actor!, observedAt: envelope.at!, recordedAt: envelope.at!, revision: assignment.revision });
      state.occurrences = state.occurrences.map((item) =>
        assignment.occurrenceIds.includes(item.id)
          ? { ...item, status: "satisfied" }
          : item,
      );
      state.allocations = state.allocations.map((item) =>
        item.assignmentId === assignment.id && item.status === "committed"
          ? { ...item, status: "finished", revision: item.revision + 1 }
          : item,
      );
      const unit = state.units.find((item) => item.id === assignment.unitId)!;
      const service = serviceFor(state, assignment.serviceId)!;
      if (service.kind === "inspection")
        assignment.completionResult =
          payload.result === "failed" ? "failed" : "passed";
      recordFulfillment(state,assignment,envelope.at!,envelope.actor!);
      unit.accessUpdatedAt ??= unit.factUpdatedAt;
      if (service.readinessEffect === "cleaned") unit.cleanliness = "cleaned";
      if (service.readinessEffect === "inspected")
        unit.cleanliness = payload.result === "failed" ? "dirty" : "ready";
      unit.factUpdatedAt = envelope.at!;
      unit.factSource = envelope.actor!;
      if (service.readinessEffect !== "none")
        state.facts.push({
          id: stableId("completion-fact", envelope.id, assignment.id),
          entityType: "unit",
          entityId: unit.id,
          key:
            service.readinessEffect === "inspected"
              ? "inspection"
              : "cleaning-completion",
          value: {
            assignmentId: assignment.id,
            result: assignment.completionResult ?? "completed",
            cleanliness: unit.cleanliness,
          },
          source: envelope.actor!,
          observedAt: envelope.at!,
          recordedAt: envelope.at!,
          revision: assignment.revision,
        });
      if (service.kind === "inspection" && payload.result === "failed") {
        const rework =
          state.services.find((item) => item.kind === "rework") ??
          state.services.find((item) => item.kind === "cleaning")!;
        state.assignments.push({
          id: stableId("rework", assignment.id),
          unitId: unit.id,
          serviceId: rework.id,
          date: assignment.date,
          estimateMinutes: rework.estimateMinutes,
          requiredSkills: rework.requiredSkills,
          preferredSkills: [],
          crewSize: rework.crewSize,
          priority: "high",
          status: "open",
          occurrenceIds: [],
          origin: "rework",
          notes:
            "Inspection failed. Correct the recorded defects before release.",
          revision: 1,
          dependsOn: [assignment.id],
          contextKey: assignment.contextKey,
        });
      }
      regenerate = true;
      break;
    }
    case "linen.record": {
      const unitId = id("unitId");
      if (!state.units.some((item) => item.id === unitId))
        throw new Error("Choose an existing unit.");
      const operation = required<
        "deliver" | "collect" | "return-check" | "return"
      >(payload, "operation");
      if (!["deliver", "collect", "return-check", "return"].includes(operation))
        throw new Error("Choose a valid linen operation.");
      state.linen.push({
        id: stableId("linen", envelope.id),
        unitId,
        assignmentId: payload.assignmentId as string | undefined,
        operation,
        notes: String(payload.notes ?? ""),
        actor: envelope.actor!,
        at: envelope.at!,
      });
      break;
    }
    case "impact.apply":
    case "impact.keep": {
      const impact = state.impacts.find((item) => item.id === id("impactId"));
      if (!impact || impact.status !== "pending")
        throw new Error(
          "This proposal is no longer pending. Refresh the current changes.",
        );
      resolveImpact(
        state,
        impact,
        envelope.type === "impact.keep",
        String(payload.reason ?? ""),
      );
      break;
    }
    case "impact.bulk": {
      if (
        payload.baseRevision !== undefined &&
        payload.baseRevision !== state.revision
      )
        throw new Error(
          "This batch preview is stale. Refresh before applying.",
        );
      const ids = required<string[]>(payload, "ids");
      const impacts = ids.map((iid) =>
        state.impacts.find((item) => item.id === iid),
      );
      if (
        !impacts.length ||
        impacts.some((item) => !item || item.status !== "pending")
      )
        throw new Error("Every selected proposal must still be pending.");
      for (const impact of impacts as ChangeProposal[])
        resolveImpact(state, impact, false, String(payload.reason ?? ""));
      break;
    }
    case "sync.policy": {
      const cleaningSync = required<"checked-out" | "always" | "never">(
        payload,
        "cleaningSync",
      );
      if (!["checked-out", "always", "never"].includes(cleaningSync))
        throw new Error("Choose a valid outbound synchronization policy.");
      if (
        payload.inboundOwnership &&
        Object.values(payload.inboundOwnership as Record<string, unknown>).some(
          (value) => !["source", "local", "review"].includes(String(value)),
        )
      )
        throw new Error(
          "Choose source, local, or review ownership for each synchronized field.",
        );
      state.integrationPolicies.hotellinx = {
        ...state.integrationPolicies.hotellinx,
        enabled: payload.enabled !== false,
        cleaningSync,
        ...(payload.inboundOwnership
          ? {
              inboundOwnership: payload.inboundOwnership as Record<
                string,
                "source" | "local" | "review"
              >,
            }
          : {}),
      };
      break;
    }
    case "sync.run": {
      const policy = state.integrationPolicies.hotellinx;
      state.syncIntents = state.syncIntents.map((intent) => {
        if (!intent.assignmentId) return intent;
        if (intent.status === "simulated" && intent.attemptedAt) return intent;
        const bound=state.assignments.find(item=>item.id===intent.assignmentId),boundCycle=bound?cycleForAssignment(state,bound):undefined;
        if(intent.stayCycleId&&(!boundCycle||boundCycle.id!==intent.stayCycleId||boundCycle.serviceRevision!==intent.cycleRevision||intent.checkoutId&&boundCycle.checkout?.id!==intent.checkoutId))return {...intent,status:'stale',reason:'The reservation checkout or service cycle changed after this update was queued.'};
        if (
          intent.bookingId &&
          intent.bookingRevision !== undefined &&
          state.bookings.find((item) => item.id === intent.bookingId)
            ?.revision !== intent.bookingRevision
        )
          return {
            ...intent,
            status: "stale",
            reason:
              "The relevant source booking changed after this update was queued. Re-evaluate the current service cycle.",
          };
        const current = syncEligibility(
          state,
          intent.assignmentId,
          envelope.at!,
        );
        if (current.status === "stale")
          return { ...intent, status: "stale", reason: current.reason };
        if(current.status==='held')return {...intent,status:'held',reason:current.reason};
        if (["held","failed","eligible"].includes(intent.status) && current.status === "eligible")
          return {
            ...intent,
            status: "simulated",
            reason:
              "Eligible retry simulated. No external system was contacted.",
            attemptedAt: envelope.at!,
          };
        return intent;
      });
      for (const assignment of state.assignments.filter(
        (item) =>
          item.status === "completed" &&
          serviceFor(state, item.serviceId)?.readinessEffect !== "none",
      )) {
        const unit = state.units.find((item) => item.id === assignment.unitId)!;
        const activeBooking = state.bookings
          .filter(
            (item) =>
              item.unitId === unit.id &&
              item.status === "confirmed" &&
              item.arrival <= (assignment.completedAt ?? envelope.at!),
          )
          .sort((a, b) => b.arrival.localeCompare(a.arrival))[0];
        const eligibility = syncEligibility(state, assignment.id, envelope.at!);
        const cycle=cycleForAssignment(state,assignment),fulfillment=cycle?currentFulfillment(state,cycle,assignment.id):undefined;
        const key = stableId(
          "sync",
          assignment.id,
          assignment.revision,
          unit.factUpdatedAt,
          policy.cleaningSync,
          cycle?.serviceRevision??0,cycle?.checkout?.id??'',fulfillment?.id??'',
          state.bookings
            .filter((item) => item.unitId === unit.id)
            .map((item) => `${item.id}:${item.revision}:${item.status}`)
            .join(","),
        );
        if (state.syncIntents.some((item) => item.id === key)) continue;
        state.syncIntents.push({
          id: key,
          connector: "hotellinx",
          unitId: unit.id,
          assignmentId: assignment.id,
          bookingId: cycle?.anchorBookingId??activeBooking?.id,
          bookingRevision: state.bookings.find(item=>item.id===(cycle?.anchorBookingId??activeBooking?.id))?.revision,
          stayCycleId:cycle?.id,cycleRevision:cycle?.serviceRevision,checkoutId:cycle?.checkout?.id,fulfillmentId:fulfillment?.id,
          fact:
            serviceFor(state, assignment.serviceId)?.readinessEffect ===
            "inspected"
              ? "guest-ready"
              : "cleaning-completed",
          value: assignment.completionResult !== "failed",
          sourceRevision: assignment.revision,
          status:
            eligibility.status === "eligible"
              ? "simulated"
              : eligibility.status,
          reason:
            eligibility.status === "eligible"
              ? "Eligible update simulated. No external system was contacted."
              : eligibility.reason,
          createdAt: envelope.at!,
          attemptedAt:
            eligibility.status === "eligible" ? envelope.at! : undefined,
        });
      }
      break;
    }
    case "import.apply": {
      const proposal = required<ImportProposal>(payload, "proposal");
      if (
        state.importRuns.some(
          (item) =>
            item.hash === proposal.hash &&
            item.connector === "quinyx" &&
            item.from === proposal.from &&
            item.to === proposal.to &&
            item.mode === proposal.mode && item.reconciliationKey===proposal.reconciliationKey,
        )
      )
        return input;
      if (proposal.baseRevision !== state.revision)
        throw new Error(
          "The workspace changed after this import preview. Rebuild the preview before applying.",
        );
      if (proposal.unresolved?.length)
        throw new Error(
          "Resolve employee mappings and invalid rows before applying this import.",
        );
      if (
        !validDate(proposal.from) ||
        !validDate(proposal.to) ||
        proposal.to < proposal.from
      )
        throw new Error("The import has an invalid date scope.");
      const incomingVersion =
        proposal.sourceUpdatedAt ?? proposal.sourceRevision;
      const versionNumber = (value?: string) =>
        value && /^\d+$/.test(value)
          ? Number(value)
          : value &&
              /^\d{4}-\d{2}-\d{2}T/.test(value) &&
              Number.isFinite(Date.parse(value))
            ? Date.parse(value)
            : undefined;
      if (
        versionNumber(incomingVersion) !== undefined &&
        state.importRuns.some(
          (item) =>
            item.connector === "quinyx" &&
            versionNumber(item.sourceRevision) !== undefined &&
            versionNumber(incomingVersion)! <
              versionNumber(item.sourceRevision)! &&
            overlap(
              `${proposal.from}T00:00:00Z`,
              `${addDays(proposal.to, 1)}T00:00:00Z`,
              `${item.from}T00:00:00Z`,
              `${addDays(item.to, 1)}T00:00:00Z`,
            ),
        )
      )
        throw new Error(
          "This source revision is older than an already imported schedule for this period.",
        );
      for (const person of proposal.people) {
        if (!person.name.trim())
          throw new Error("An imported employee is missing a name.");
        if (
          Object.entries(person.externalIds).some(
            ([source, external]) =>
              external &&
              state.people.some(
                (other) =>
                  other.id !== person.id &&
                  other.externalIds[source] === external,
              ),
          )
        )
          throw new Error(
            "An external employee identifier maps to multiple people.",
          );
        state.people = put(state.people, person);
      }
      for (const removeId of proposal.removeIds) {
        const interval = state.availability.find(
          (item) => item.id === removeId,
        );
        if (
          proposal.mode !== "snapshot" ||
          !interval ||
          interval.source !== "quinyx" ||
          interval.kind === "unavailable" ||
          interval.localOverride ||
          interval.businessDate < proposal.from ||
          interval.businessDate > proposal.to
        )
          throw new Error(
            "Only source-owned intervals inside a complete snapshot scope may be removed.",
          );
      }
      for(const replaceId of proposal.replaceSourceIds??[]){
        const interval=state.availability.find(item=>item.id===replaceId);
        if(!interval||interval.source!=='quinyx'||interval.kind==='unavailable'||interval.localOverride||!proposal.sourceSnapshot?.rows.some(row=>row.personId===interval.personId&&row.businessDate===interval.businessDate))throw new Error('Only unchanged source work hours on represented import days may be replaced.');
      }
      const removeIds=new Set([...proposal.removeIds,...(proposal.replaceSourceIds??[])]);
      state.availability = state.availability.filter(
        (item) => !removeIds.has(item.id),
      );
      for (const interval of proposal.intervals) {
        if (
          interval.businessDate < proposal.from ||
          interval.businessDate > proposal.to
        )
          throw new Error(
            "An imported interval lies outside the declared scope.",
          );
        const old = state.availability.find((item) => item.id === interval.id);
        if (old?.kind === "unavailable" && interval.kind !== "unavailable")
          throw new Error("An import cannot turn unavailability into working time. Edit the workday explicitly.");
        if (
          old?.localOverride &&
          interval.localOverride !== false &&
          !proposal.reviewedOverrideIds?.includes(interval.id) &&
          (old.start !== interval.start || old.end !== interval.end)
        )
          throw new Error(
            "Review the local hour override before replacing it with source values.",
          );
        if (
          old?.sourceUpdatedAt &&
          interval.sourceUpdatedAt &&
          interval.sourceUpdatedAt < old.sourceUpdatedAt
        )
          throw new Error(
            "An incoming employee schedule is older than its current source revision.",
          );
        state.availability = put(state.availability, interval);
      }
      validateAvailability(state);
      state.importRuns.push({
        id: proposal.id,
        connector: "quinyx",
        name: proposal.name,
        hash: proposal.hash,
        from: proposal.from,
        to: proposal.to,
        mode: proposal.mode,
        sourceRevision: proposal.sourceUpdatedAt ?? proposal.sourceRevision,
        reconciliationKey:proposal.reconciliationKey,
        at: envelope.at!,
        ...proposal.counts,
        notes: proposal.notes,
      });
      if(proposal.sourceSnapshot)state=applyRosterSnapshot(state,proposal.sourceSnapshot,envelope.at!);
      break;
    }
    default:
      throw new Error(`Unsupported operation: ${envelope.type}`);
  }
  if (regenerate)
    state = reconcileRules(state, regenerationRange ?? rangeFor(state, envelope.at), envelope.at);
  if (envelope.type === "work.complete") {
    const completed = state.assignments.find(
      (item) => item.id === payload.assignmentId,
    );
    if (
      completed &&
      serviceFor(state, completed.serviceId)?.readinessEffect === "cleaned" &&
      state.assignments.some(
        (item) =>
          item.unitId === completed.unitId &&
          serviceFor(state, item.serviceId)?.kind === "inspection" &&
          !["completed", "declined", "cancelled"].includes(item.status) &&
          item.dependsOn?.includes(completed.id),
      )
    )
      state.units.find((item) => item.id === completed.unitId)!.cleanliness =
        "inspection-needed";
    state.handovers = state.handovers?.map((item) =>
      item.assignmentId === payload.assignmentId
        ? { ...item, status: "resolved" }
        : item,
    );
  }
  state.revision = input.revision + 1;
  state = addFactImpacts(input, state, envelope);
  state.idempotencyKeys = [...state.idempotencyKeys.slice(-1999), envelope.id];
  const targetIds = [
    payload.unitId,
    payload.personId,
    payload.shiftId,
    payload.assignmentId,
    payload.bookingId,
    payload.stayId,
    payload.impactId,
    ...(Array.isArray(payload.assignmentIds) ? payload.assignmentIds : []),
    ...Object.values(payload)
      .filter(
        (value) => value && typeof value === "object" && !Array.isArray(value),
      )
      .map((value) => (value as { id?: unknown }).id),
  ].filter((value): value is string => typeof value === "string");
  const auditShift =
    typeof payload.shiftId === "string"
      ? payload.shiftId
      : ((payload.shift as Shift | undefined)?.id ??
        (payload.allocation as AssignmentAllocation | undefined)?.shiftId ??
        (typeof payload.assignmentId === "string"
          ? input.allocations.find(
              (item) =>
                item.assignmentId === payload.assignmentId &&
                item.status === "committed",
            )?.shiftId
          : undefined));
  state.audit.push({
    id: envelope.id,
    type: envelope.type,
    actor: envelope.actor!,
    at: envelope.at!,
    summary: envelope.type.replace(/\./g, " "),
    revision: state.revision,
    targetIds: [...new Set(targetIds)],
    shiftId: auditShift,
  });
  validateWorkspace(state);
  return state;
}
